import { startVisibleRealtime } from "./visible-realtime";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import listPlugin from "@fullcalendar/list";
import zhCnLocale from "@fullcalendar/core/locales/zh-cn";
import { reconcileFetchedEvents, type ConfirmedMutation } from "./event-reconciliation";
import { disabledRangesForWorkday, slotPositionForMinute, type StoreDisabledTime } from "./store-disabled-times";
import { STORE_SUBCOLUMNS, displayedStores, scheduledStores, type DayLocationPrefs, type LocationSpan, type StoreSubcolumn } from "./day-location-columns";
import type { CalendarApi, DateSelectArg, DatesSetArg, EventApi, EventClickArg, EventDropArg, EventInput } from "@fullcalendar/core";
import type { DateClickArg, EventResizeDoneArg } from "@fullcalendar/interaction";
import {
  AlertTriangle, AudioLines, BarChart3, Bell, Calculator, CalendarDays, Check, ChevronDown, ChevronLeft, ChevronRight,
  Columns3, Copy, Download, FileSpreadsheet, Filter, Handshake, LogOut, Menu, Plus, Printer, RefreshCw,
  RotateCcw, Save, Search, Settings, ShieldCheck, Trash2, X
} from "lucide-react";

type Role = "admin" | "manager" | "scheduler" | "staff" | "viewer";
type PricingMode = "time" | "quantity";
type Branch = { id: number; name: string; color: string; active: boolean };
type User = { id: number; username: string; displayName: string; role: Role; branchId: number | null; canEditEstimatedPrice: boolean; active: boolean };
type Bootstrap = {
  user: User;
  csrfToken: string;
  branches: Branch[];
  users: User[];
  workTypes: string[];
  workTypeColors: Record<string, string>;
  storeNames: string[];
  storeColors: Record<string, string>;
  storeDisabledTimes: StoreDisabledTime[];
  projectNames: string[];
  projectSummaries: ProjectReportSummary[];
  roles: Role[];
};
type EventRecord = {
  id?: number;
  title: string;
  workType: string;
  workTypes: string[];
  startAt: string;
  endAt: string;
  branchId: number;
  employeeId: number | null;
  participantIds: number[];
  participantWorkTypes?: Record<number, string[]>;
  clientProject: string;
  location: string;
  storeName: string;
  pricingMode: PricingMode;
  unitPrice: number | null;
  totalPrice: number | null;
  projectEstimatedTotal?: number | null;
  projectEstimatedTotalOverride?: number | null;
  collaborationRootEventId?: number | null;
  isCollaborative?: boolean;
  notes: string;
  status: "scheduled" | "completed" | "cancelled";
  version?: number;
  createdBy?: number;
  updatedBy?: number;
  createdAt?: string;
  updatedAt?: string;
};
type CalendarUndoAction =
  | { kind: "create"; event: EventRecord }
  | { kind: "delete"; event: EventRecord }
  | { kind: "update"; before: EventRecord; after: EventRecord };
type ReportRow = EventRecord & {
  id: number;
  employeeName: string;
  creatorName: string;
  durationMinutes: number;
};
type ProjectReportSummary = { project: string; count: number; totalMinutes: number; pricingMode: PricingMode; unitPrice: number | null; estimatedTotal: number; estimatedTotalOverride: number | null; totalAmount: number };
type ReportSummary = { count: number; totalMinutes: number; estimatedTotal: number; totalAmount: number; byType: Record<string, number>; byProject: ProjectReportSummary[] };
type ScheduleExportColumnKey = "date" | "weekday" | "startTime" | "endTime" | "duration" | "title" | "workTypes" | "employee" | "participants" | "store" | "status" | "pricingMode" | "unitPrice" | "estimatedTotal" | "totalAmount" | "notes";
const scheduleExportColumnOptions: Array<{ key: ScheduleExportColumnKey; label: string }> = [
  { key: "date", label: "日期" }, { key: "weekday", label: "星期" }, { key: "startTime", label: "开始时间" },
  { key: "endTime", label: "结束时间" }, { key: "duration", label: "时长（小时）" }, { key: "title", label: "日程标题" },
  { key: "workTypes", label: "工作类型" }, { key: "employee", label: "负责人" }, { key: "participants", label: "参与人员" },
  { key: "store", label: "店铺" }, { key: "status", label: "状态" }, { key: "pricingMode", label: "计价方式" },
  { key: "unitPrice", label: "项目单价" }, { key: "estimatedTotal", label: "项目预估总价" },
  { key: "totalAmount", label: "项目实收总金额" }, { key: "notes", label: "备注" }
];
const defaultScheduleExportColumns: ScheduleExportColumnKey[] = ["date", "weekday", "startTime", "endTime", "duration", "title", "workTypes", "employee", "participants", "store", "status", "notes"];
type PayrollSettings = {
  employee: User;
  baseSalary: number;
  employeeSocialSecurityContribution: number;
  commissionRates: Record<string, number>;
};
type PayrollCalculationRow = {
  id: number;
  title: string;
  clientProject: string;
  startAt: string;
  endAt: string;
  storeName: string;
  workTypes: string[];
  status: EventRecord["status"];
  receivedAmount: number;
  projectAmountCounted: boolean;
  defaultCalculationAmount: number;
  defaultCalculationRate: number;
  actualCalculationAmount: number | null;
  actualCalculationRate: number | null;
  effectiveCalculationAmount: number;
  effectiveCalculationRate: number;
  commission: number;
};
type PayrollCalculation = {
  settings: PayrollSettings;
  rows: PayrollCalculationRow[];
  holidayOvertime: {
    days: { date: string; name: string; enabled: boolean; updatedAt: string | null }[];
    periodDays: number; overtimeDays: number; dailyBaseSalary: number; extraSalary: number;
    unverifiedYears: number[]; sources: {title: string; url: string}[];
  };
  summary: { holidayOvertimeExtra: number; baseSalary: number; commissionTotal: number; employeeSocialSecurityContribution: number; salaryTotal: number; includedRecords: number };
};
type RecentEventChange = {
  id: number;
  eventId: number | null;
  action: "create" | "update" | "delete";
  actorName: string;
  title: string;
  startAt: string | null;
  endAt: string | null;
  clientProject: string;
  employeeName: string;
  createdAt: string;
};
type PendingAssignment = {
  eventId: number;
  title: string;
  startAt: string;
  endAt: string;
  clientProject: string;
  assignedByName: string;
  assignedAt: string;
};
type ReviewTask = {
  id: string;
  kind: "review.ready" | "review.changes_requested" | string;
  projectId: string;
  projectTitle: string;
  title: string;
  body: string;
  createdAt: string;
  actionUrl: string;
  actionLabel: string;
};
type NotificationFeed = {
  retentionDays: number;
  recentChanges: RecentEventChange[];
  pendingAssignments: PendingAssignment[];
  reviewTasks: ReviewTask[];
};

const CREATE_DRAG_MIN_DISTANCE = 10;
const EVENT_DEFAULTS_STORAGE_PREFIX = "ale-calendar-event-defaults-v1";

function storeSubcolumnIndex(storeName: string) {
  return STORE_SUBCOLUMNS.findIndex((name) => name === storeName);
}

function storeMarkerColor(storeName: string, bootstrap: Bootstrap) {
  return bootstrap.storeColors[storeName] || "#a8afb9";
}

type EventDefaults = Pick<EventRecord, "employeeId" | "storeName">;
type EventHoverPreviewState = {
  value: EventRecord;
  timeText: string;
  style: React.CSSProperties;
};

function eventDefaultsStorageKey(userId: number) {
  return `${EVENT_DEFAULTS_STORAGE_PREFIX}:${userId}`;
}

function loadEventDefaults(bootstrap: Bootstrap): EventDefaults {
  const fallback: EventDefaults = { employeeId: bootstrap.user.id, storeName: "" };
  try {
    const raw = window.localStorage.getItem(eventDefaultsStorageKey(bootstrap.user.id));
    if (!raw) return fallback;
    const parsed = JSON.parse(raw) as Partial<EventDefaults>;
    const hasRememberedEmployee = Object.prototype.hasOwnProperty.call(parsed, "employeeId");
    const rememberedEmployeeId = parsed.employeeId === null ? null : Number(parsed.employeeId);
    const employeeIsValid = rememberedEmployeeId === null || (
      Number.isInteger(rememberedEmployeeId)
      && bootstrap.users.some((user) => user.id === rememberedEmployeeId && user.active)
    );
    return {
      employeeId: bootstrap.user.role === "staff"
        ? bootstrap.user.id
        : hasRememberedEmployee && employeeIsValid ? rememberedEmployeeId : fallback.employeeId,
      storeName: typeof parsed.storeName === "string" && bootstrap.storeNames.includes(parsed.storeName) ? parsed.storeName : ""
    };
  } catch {
    return fallback;
  }
}

function rememberEventDefaults(userId: number, value: EventDefaults) {
  try {
    window.localStorage.setItem(eventDefaultsStorageKey(userId), JSON.stringify(value));
  } catch {
    // 浏览器禁用本地存储时仍允许正常创建日程，只是不记住默认值。
  }
}

class ApiError extends Error {
  status: number;
  data: Record<string, unknown>;
  constructor(status: number, data: Record<string, unknown>) {
    super(String(data.error || "请求失败"));
    this.status = status;
    this.data = data;
  }
}

async function request<T>(url: string, options: RequestInit = {}, csrfToken?: string): Promise<T> {
  const headers = new Headers(options.headers);
  if (options.body && !headers.has("Content-Type")) headers.set("Content-Type", "application/json");
  if (csrfToken) headers.set("X-CSRF-Token", csrfToken);
  const response = await fetch(url, { ...options, headers, credentials: "same-origin" });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new ApiError(response.status, data);
  return data as T;
}

function localDateTime(iso: string) {
  const date = new Date(iso);
  const offset = date.getTimezoneOffset();
  return new Date(date.getTime() - offset * 60_000).toISOString().slice(0, 16);
}

function toIso(value: string) {
  return new Date(value).toISOString();
}

function dateInput(date = new Date()) {
  const offset = date.getTimezoneOffset();
  return new Date(date.getTime() - offset * 60_000).toISOString().slice(0, 10);
}

function addLocalDays(date: Date, days: number) {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

function SidebarCalendar({ selectedDate, onSelect }: { selectedDate: string; onSelect: (value: string) => void }) {
  const selected = new Date(`${selectedDate}T12:00:00`);
  const [visibleMonth, setVisibleMonth] = useState(() => new Date(selected.getFullYear(), selected.getMonth(), 1));

  useEffect(() => {
    setVisibleMonth(new Date(selected.getFullYear(), selected.getMonth(), 1));
  }, [selectedDate]);

  const firstVisibleDay = new Date(visibleMonth.getFullYear(), visibleMonth.getMonth(), 1 - visibleMonth.getDay());
  const dates = Array.from({ length: 42 }, (_, index) => addLocalDays(firstVisibleDay, index));
  const today = dateInput();
  const monthTitle = `${visibleMonth.getFullYear()}年${visibleMonth.getMonth() + 1}月`;

  function shiftMonth(months: number) {
    setVisibleMonth((current) => new Date(current.getFullYear(), current.getMonth() + months, 1));
  }

  return (
    <section className="sidebar-calendar" aria-label="日期导航">
      <header>
        <button type="button" aria-label="上个月" onClick={() => shiftMonth(-1)}><ChevronLeft size={15} /></button>
        <strong>{monthTitle}</strong>
        <button type="button" aria-label="下个月" onClick={() => shiftMonth(1)}><ChevronRight size={15} /></button>
      </header>
      <div className="sidebar-calendar-weekdays" aria-hidden="true">
        {['日', '一', '二', '三', '四', '五', '六'].map((day) => <span key={day}>{day}</span>)}
      </div>
      <div className="sidebar-calendar-days">
        {dates.map((date) => {
          const value = dateInput(date);
          const outside = date.getMonth() !== visibleMonth.getMonth();
          return (
            <button
              type="button"
              key={value}
              className={`${outside ? "outside" : ""} ${value === selectedDate ? "selected" : ""} ${value === today ? "today" : ""}`.trim()}
              aria-label={`${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日`}
              aria-current={value === selectedDate ? "date" : undefined}
              onClick={() => onSelect(value)}
            >{date.getDate()}</button>
          );
        })}
      </div>
    </section>
  );
}

function localDayDistance(start: Date, end: Date) {
  const first = new Date(start.getFullYear(), start.getMonth(), start.getDate()).getTime();
  const second = new Date(end.getFullYear(), end.getMonth(), end.getDate()).getTime();
  return Math.round((second - first) / 86_400_000);
}

function rollingVisibleRange(currentDate: Date) {
  const center = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate());
  return { start: addLocalDays(center, -21), end: addLocalDays(center, 35) };
}

function rollingTitle(date: Date) {
  return `${date.getFullYear()}年${date.getMonth() + 1}月`;
}

function compactTimeRange(startIso: string, endIso: string) {
  const start = new Date(startIso);
  const end = new Date(endIso);
  const date = new Intl.DateTimeFormat("zh-CN", { month: "numeric", day: "numeric", weekday: "short" }).format(start);
  const time = new Intl.DateTimeFormat("zh-CN", { hour: "2-digit", minute: "2-digit", hour12: false });
  return `${date} ${time.format(start)}–${time.format(end)}`;
}

function moneyText(value: number | null) {
  return value == null ? "" : new Intl.NumberFormat("zh-CN", { style: "currency", currency: "CNY" }).format(value);
}

function projectSummaryForName(bootstrap: Bootstrap, projectName: string) {
  const normalized = projectName.trim().toLocaleLowerCase("zh-CN");
  return normalized
    ? bootstrap.projectSummaries.find((item) => item.project.trim().toLocaleLowerCase("zh-CN") === normalized) || null
    : null;
}

function projectFormChange(current: EventRecord, projectName: string, bootstrap: Bootstrap): EventRecord {
  const previousSummary = projectSummaryForName(bootstrap, current.clientProject);
  const nextSummary = projectSummaryForName(bootstrap, projectName);
  return {
    ...current,
    clientProject: projectName,
    pricingMode: nextSummary ? nextSummary.pricingMode : previousSummary ? "time" : current.pricingMode,
    unitPrice: nextSummary ? nextSummary.unitPrice : previousSummary ? null : current.unitPrice,
    totalPrice: nextSummary ? nextSummary.totalAmount : previousSummary ? null : current.totalPrice,
    projectEstimatedTotal: nextSummary ? nextSummary.estimatedTotal : null,
    projectEstimatedTotalOverride: nextSummary ? nextSummary.estimatedTotalOverride : null
  };
}

function projectedProjectTotal(original: EventRecord, current: EventRecord, bootstrap: Bootstrap) {
  const projectName = current.clientProject.trim();
  if (!projectName) return null;
  if (current.projectEstimatedTotalOverride != null) return current.projectEstimatedTotalOverride;
  if (current.unitPrice == null) return null;
  const summary = projectSummaryForName(bootstrap, projectName);
  const projectKey = projectName.toLocaleLowerCase("zh-CN");
  const originalIncluded = Boolean(
    original.id
    && original.status !== "cancelled"
    && original.clientProject.trim().toLocaleLowerCase("zh-CN") === projectKey
  );
  const nextIncluded = current.status !== "cancelled";
  const originalMinutes = Math.max(0, Math.round((new Date(original.endAt).getTime() - new Date(original.startAt).getTime()) / 60_000));
  const nextMinutes = Math.max(0, Math.round((new Date(current.endAt).getTime() - new Date(current.startAt).getTime()) / 60_000));
  const totalMinutes = Math.max(0, (summary?.totalMinutes || 0) - (originalIncluded ? originalMinutes : 0) + (nextIncluded ? nextMinutes : 0));
  const originalCount = originalIncluded && !original.collaborationRootEventId ? 1 : 0;
  const nextCount = nextIncluded && !current.collaborationRootEventId ? 1 : 0;
  const totalCount = Math.max(0, (summary?.count || 0) - originalCount + nextCount);
  return projectEstimate(current.pricingMode, current.unitPrice, totalCount, totalMinutes);
}

function projectEstimate(pricingMode: PricingMode, unitPrice: number | null, count: number, totalMinutes: number) {
  if (unitPrice == null) return null;
  return pricingMode === "quantity"
    ? Math.round(unitPrice * count * 100) / 100
    : Math.round(unitPrice * totalMinutes / 60 * 100) / 100;
}

function relativeTimeText(iso: string) {
  const differenceMinutes = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60_000));
  if (differenceMinutes < 1) return "刚刚";
  if (differenceMinutes < 60) return `${differenceMinutes} 分钟前`;
  const hours = Math.floor(differenceMinutes / 60);
  if (hours < 24) return `${hours} 小时前`;
  return `${Math.floor(hours / 24)} 天前`;
}

function formatHours(minutes: number) {
  const hours = minutes / 60;
  return Number.isInteger(hours) ? `${hours} 小时` : `${hours.toFixed(1)} 小时`;
}

function roleLabel(role: Role) {
  if (role === "admin") return "管理员";
  if (role === "manager" || role === "scheduler") return "店长";
  if (role === "viewer") return "只读账号";
  return "普通账号";
}

function eventWorkTypes(value: Pick<EventRecord, "workType" | "workTypes">) {
  return value.workTypes?.length ? value.workTypes : [value.workType].filter(Boolean);
}

function isAdminCreatedEvent(value: EventRecord, bootstrap: Bootstrap) {
  return bootstrap.users.some((user) => user.id === value.createdBy && user.role === "admin");
}

function canFullyModifyEvent(value: EventRecord, bootstrap: Bootstrap) {
  if (["admin", "manager", "scheduler"].includes(bootstrap.user.role)) return true;
  return bootstrap.user.role === "staff" && value.employeeId === bootstrap.user.id;
}

function canMoveOrCopyEvent(value: EventRecord, bootstrap: Bootstrap) {
  return canFullyModifyEvent(value, bootstrap)
    || (bootstrap.user.role === "staff" && isAdminCreatedEvent(value, bootstrap));
}

function canDeleteEvent(bootstrap: Bootstrap) {
  return ["admin", "manager", "scheduler"].includes(bootstrap.user.role);
}

function workTypeBackground(types: string[], bootstrap: Bootstrap) {
  const colors = [...new Set(types.map((type) => bootstrap.workTypeColors[type]).filter(Boolean))];
  if (colors.length <= 1) return colors[0] || bootstrap.workTypeColors[bootstrap.workTypes[0]];
  return `linear-gradient(135deg, ${colors.map((color, index) => `${color} ${(index / (colors.length - 1)) * 100}%`).join(", ")})`;
}

function translucentColor(color: string, alpha: number) {
  const match = /^#([0-9a-f]{6})$/i.exec(color);
  if (!match) return `rgba(34, 43, 54, ${alpha})`;
  const number = Number.parseInt(match[1], 16);
  return `rgba(${number >> 16}, ${(number >> 8) & 255}, ${number & 255}, ${alpha})`;
}

function workTypeTranslucentBackground(types: string[], bootstrap: Bootstrap) {
  const colors = [...new Set(types.map((type) => bootstrap.workTypeColors[type]).filter(Boolean))];
  const source = colors.length ? colors : [bootstrap.workTypeColors[bootstrap.workTypes[0]]];
  const translucent = source.map((color) => translucentColor(color, .84));
  if (translucent.length === 1) return translucent[0];
  return `linear-gradient(135deg, ${translucent.map((color, index) => `${color} ${(index / (translucent.length - 1)) * 100}%`).join(", ")})`;
}

function calendarEventInput(item: EventRecord, bootstrap: Bootstrap, options: { id?: string; pending?: boolean } = {}): EventInput {
  const classNames = item.status === "cancelled" ? ["event-cancelled"] : item.status === "completed" ? ["event-completed"] : [];
  if (options.pending) classNames.push("event-copy-saving");
  return {
    id: options.id || String(item.id),
    title: item.title,
    start: item.startAt,
    end: item.endAt,
    backgroundColor: bootstrap.workTypeColors[eventWorkTypes(item)[0]],
    borderColor: bootstrap.workTypeColors[eventWorkTypes(item)[0]],
    textColor: "#ffffff",
    classNames,
    editable: canMoveOrCopyEvent(item, bootstrap),
    extendedProps: item
  };
}

function calendarEventsById(calendar: CalendarApi | undefined, id: string | number): EventApi[] {
  const normalizedId = String(id);
  return calendar?.getEvents().filter((event) => event.id === normalizedId) || [];
}

function removeCalendarEventsById(calendar: CalendarApi | undefined, id: string | number): void {
  for (const event of calendarEventsById(calendar, id)) event.remove();
}

function addCalendarEventToSource(calendar: CalendarApi | undefined, input: EventInput): EventApi | null {
  if (!calendar) return null;
  const source = calendar.getEventSources()[0];
  return source ? calendar.addEvent(input, source) : calendar.addEvent(input);
}

function replaceCalendarEvent(calendar: CalendarApi | undefined, input: EventInput): EventApi | null {
  if (input.id != null) removeCalendarEventsById(calendar, String(input.id));
  return addCalendarEventToSource(calendar, input);
}

function reconcileCalendarEventId(calendar: CalendarApi | undefined, id: string | number): void {
  const matches = calendarEventsById(calendar, id);
  if (matches.length <= 1) return;
  const keep = [...matches].sort((first, second) => {
    const firstValue = first.extendedProps as EventRecord;
    const secondValue = second.extendedProps as EventRecord;
    return Number(secondValue.version || 0) - Number(firstValue.version || 0)
      || String(secondValue.updatedAt || "").localeCompare(String(firstValue.updatedAt || ""));
  })[0];
  for (const event of matches) if (event !== keep) event.remove();
}

type CrossOverlapEntry = {
  harness: HTMLElement;
  eventElement: HTMLElement;
  inner: HTMLElement;
  start: number;
  end: number;
  singleDay: boolean;
  wasCross: boolean;
};

function localDayKey(timestamp: number) {
  const date = new Date(timestamp);
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
}

function pathNumber(value: number) {
  return Number(value.toFixed(2));
}

const CROSS_OVERLAP_STEM_WIDTH = 10;
const ROLLING_TIME_AXIS_WIDTH = 46;

type RollingDayColumn = {
  date: string;
  element: HTMLElement;
  width: number;
};

// storeName === null counts every event in the day, for the merged column.
function maximumConcurrentEvents(root: HTMLElement, storeName: string | null) {
  const points = Array.from(root.querySelectorAll<HTMLElement>(".event-inner[data-start-at][data-end-at]"))
    .flatMap((inner) => {
      if (storeName !== null && inner.dataset.storeName !== storeName) return [];
      const start = new Date(inner.dataset.startAt || "").getTime();
      const end = new Date(inner.dataset.endAt || "").getTime();
      if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return [];
      return [{ time: start, delta: 1 }, { time: end, delta: -1 }];
    })
    // End points are handled before starts at the same instant, so adjacent
    // events (09:30-11:00 and 11:00-12:30) do not widen the day.
    .sort((first, second) => first.time - second.time || first.delta - second.delta);
  let active = 0;
  let maximum = 0;
  for (const point of points) {
    active += point.delta;
    maximum = Math.max(maximum, active);
  }
  return maximum;
}

function locationSpansForDay(column: HTMLElement): LocationSpan[] {
  return Array.from(column.querySelectorAll<HTMLElement>(".event-inner[data-store-name]")).map((inner) => ({
    store: inner.dataset.storeName || "",
    start: new Date(inner.dataset.startAt || "").getTime(),
    end: new Date(inner.dataset.endAt || "").getTime()
  }));
}

function scheduledStoresForDay(column: HTMLElement) {
  return scheduledStores(locationSpansForDay(column));
}

const DAY_LOCATION_PREFS_KEY = "ale-calendar-day-locations-v1";
const DAY_LOCATION_PREFS_KEEP_DAYS = 60;

// Per-browser view preference: which venue columns a day was expanded to show.
function loadDayLocationPrefs(): DayLocationPrefs {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(DAY_LOCATION_PREFS_KEY) || "{}");
    const oldest = dateInput(new Date(Date.now() - DAY_LOCATION_PREFS_KEEP_DAYS * 86_400_000));
    const prefs: DayLocationPrefs = {};
    for (const [date, stores] of Object.entries(parsed as Record<string, unknown>)) {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || date < oldest || !Array.isArray(stores)) continue;
      const valid = STORE_SUBCOLUMNS.filter((name) => stores.includes(name));
      if (valid.length) prefs[date] = valid;
    }
    return prefs;
  } catch {
    return {};
  }
}

function saveDayLocationPrefs(prefs: DayLocationPrefs) {
  try {
    window.localStorage.setItem(DAY_LOCATION_PREFS_KEY, JSON.stringify(prefs));
  } catch {
    // Storage can be unavailable (private mode, quota); the choice then lasts for this session only.
  }
}

function displayedStoresForDay(column: HTMLElement, prefs: DayLocationPrefs): StoreSubcolumn[] {
  return displayedStores(locationSpansForDay(column), prefs[column.dataset.date || ""]);
}

function rollingDayColumns(root: HTMLElement | null, baseWidth: number, overlapExpansionPercent: number, prefs: DayLocationPrefs): RollingDayColumn[] {
  if (!root) return [];
  return Array.from(root.querySelectorAll<HTMLElement>(".fc-timegrid-cols .fc-timegrid-col[data-date]"))
    .map((element) => {
      const activeStores = displayedStoresForDay(element, prefs);
      const concurrentEvents = activeStores.length
        ? Math.max(1, ...activeStores.map((name) => maximumConcurrentEvents(element, name)))
        : Math.max(1, maximumConcurrentEvents(element, null));
      return {
        date: element.dataset.date || "",
        element,
        width: Math.round(baseWidth * Math.max(1, activeStores.length) * (1 + (concurrentEvents - 1) * overlapExpansionPercent / 100))
      };
    })
    .filter((column) => Boolean(column.date));
}

function applyRollingDayColumnWidths(root: HTMLElement | null, baseWidth: number, overlapExpansionPercent: number, prefs: DayLocationPrefs) {
  if (!root?.classList.contains("rolling-calendar")) return false;
  const columns = rollingDayColumns(root, baseWidth, overlapExpansionPercent, prefs);
  if (!columns.length) return false;
  const widths = new Map(columns.map((column) => [column.date, column.width]));
  const totalWidth = ROLLING_TIME_AXIS_WIDTH + columns.reduce((sum, column) => sum + column.width, 0);
  const previousWidth = root.style.getPropertyValue("--rolling-calendar-width");
  const nextWidth = `${totalWidth}px`;

  root.style.setProperty("--rolling-calendar-width", nextWidth);
  for (const cell of root.querySelectorAll<HTMLElement>("[data-date]")) {
    const width = widths.get(cell.dataset.date || "");
    if (!width) continue;
    cell.style.width = `${width}px`;
    cell.style.minWidth = `${width}px`;
    cell.style.maxWidth = `${width}px`;
    cell.dataset.aleDayWidth = String(width);

    if (!(cell instanceof HTMLTableCellElement)) continue;
    const table = cell.closest("table");
    const col = table?.querySelectorAll<HTMLTableColElement>("colgroup col")[cell.cellIndex];
    if (!col) continue;
    col.style.width = `${width}px`;
    col.style.minWidth = `${width}px`;
    col.dataset.aleDayWidth = String(width);
  }
  return previousWidth !== nextWidth;
}

function clearRollingDayColumnWidths(root: HTMLElement | null) {
  if (!root) return;
  root.style.removeProperty("--rolling-calendar-width");
  for (const element of root.querySelectorAll<HTMLElement>("[data-ale-day-width]")) {
    element.style.removeProperty("width");
    element.style.removeProperty("min-width");
    element.style.removeProperty("max-width");
    delete element.dataset.aleDayWidth;
  }
}

function applyStoreDisabledShades(column: HTMLElement, activeStores: readonly string[], rules: StoreDisabledTime[], filteredStore: string) {
  const frame = column.querySelector<HTMLElement>(".fc-timegrid-col-frame");
  if (!frame) return;
  frame.querySelectorAll(":scope > .store-disabled-shade").forEach((shade) => shade.remove());
  const date = column.dataset.date || "";
  if (!date || !rules.length) return;
  const stores = activeStores.length ? activeStores : filteredStore ? [filteredStore] : [];
  if (!stores.length) return;
  // Use the same measured slat boundaries as FullCalendar's event geometry.
  // The column frame can stretch beyond the slat table, so percentages of its
  // height drift away from the clock grid (especially near the end of the day).
  const frameTop = frame.getBoundingClientRect().top + frame.clientTop;
  const slots = Array.from(column.closest(".fc-timegrid-body")?.querySelectorAll<HTMLElement>(".fc-timegrid-slot-lane[data-time]") || [])
    .map((slot) => {
      const time = slot.dataset.time!;
      const rect = slot.getBoundingClientRect();
      const minute = Number(time.slice(0, 2)) * 60 + Number(time.slice(3, 5));
      return { minute: minute < 420 ? minute + 1440 : minute, top: rect.top - frameTop, bottom: rect.bottom - frameTop };
    });
  if (!slots.length) return;
  const weekday = new Date(`${date}T12:00:00`).getDay();
  const fragment = document.createDocumentFragment();
  for (const rule of rules) {
    const index = stores.indexOf(rule.storeName);
    if (index < 0) continue;
    for (const span of disabledRangesForWorkday(rule, date, weekday)) {
      const top = slotPositionForMinute(slots, span[0]);
      const bottom = slotPositionForMinute(slots, span[1]);
      if (bottom <= top) continue;
      const shade = document.createElement("div");
      shade.className = "store-disabled-shade";
      shade.style.top = `${top}px`;
      shade.style.height = `${bottom - top}px`;
      shade.style.left = `${(index / stores.length) * 100}%`;
      shade.style.width = `${100 / stores.length}%`;
      fragment.appendChild(shade);
    }
  }
  frame.prepend(fragment);
}

// Below this day width the header has no room for the expand button.
const COMPACT_DAY_WIDTH = 96;

function applyLocationSubcolumns(root: HTMLElement | null, bootstrap: Bootstrap | null, filteredStore: string, prefs: DayLocationPrefs) {
  if (!root?.classList.contains("location-columns")) return;
  const headers = new Map(Array.from(root.querySelectorAll<HTMLElement>(".fc-col-header-cell[data-date]"))
    .map((cell) => [cell.dataset.date, cell]));
  for (const column of root.querySelectorAll<HTMLElement>(".fc-timegrid-col[data-date]")) {
    const activeStores = displayedStoresForDay(column, prefs);
    if (bootstrap) applyStoreDisabledShades(column, activeStores, bootstrap.storeDisabledTimes, filteredStore);
    const locationCount = Math.max(1, activeStores.length);
    column.dataset.locationCount = String(locationCount);
    const headerCell = headers.get(column.dataset.date);
    headerCell?.classList.toggle("location-expanded", Boolean(prefs[column.dataset.date || ""]));
    headerCell?.classList.toggle("compact-day", Number(headerCell.dataset.aleDayWidth) > 0 && Number(headerCell.dataset.aleDayWidth) < COMPACT_DAY_WIDTH);
    const header = headerCell?.querySelector<HTMLElement>(".location-day-stores");
    if (header) {
      header.style.gridTemplateColumns = `repeat(${locationCount}, minmax(0, 1fr))`;
      for (const label of header.querySelectorAll<HTMLElement>("[data-store-name]")) {
        label.hidden = !activeStores.includes(label.dataset.storeName as StoreSubcolumn);
        label.classList.toggle("first-active-location", label.dataset.storeName === activeStores[0]);
        label.classList.toggle("last-active-location", label.dataset.storeName === activeStores[activeStores.length - 1]);
      }
    }
    const container = column.querySelector<HTMLElement>(".fc-timegrid-col-events");
    if (!container) continue;
    const entries = Array.from(container.querySelectorAll<HTMLElement>(":scope > .fc-timegrid-event-harness"))
      .flatMap((harness) => {
        const inner = harness.querySelector<HTMLElement>(".event-inner[data-start-at][data-end-at]");
        if (!inner) return [];
        const start = new Date(inner.dataset.startAt || "").getTime();
        const end = new Date(inner.dataset.endAt || "").getTime();
        if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return [];
        return [{ harness, start, end, storeIndex: storeSubcolumnIndex(inner.dataset.storeName || "") }];
      });
    for (const entry of entries) {
      entry.harness.style.setProperty("margin-right", "0", "important");
      // With no venue columns every card, unspecified venue included, is laid out in the merged column.
      if (entry.storeIndex < 0 && activeStores.length) {
        entry.harness.style.setProperty("left", "1px", "important");
        entry.harness.style.setProperty("right", "1px", "important");
      }
    }
    const slots = activeStores.length
      ? STORE_SUBCOLUMNS.flatMap((name, storeIndex) => {
          const slot = activeStores.indexOf(name);
          return slot < 0 ? [] : [{ locationSlot: slot, members: entries.filter((entry) => entry.storeIndex === storeIndex) }];
        })
      : [{ locationSlot: 0, members: entries }];
    for (const { locationSlot, members } of slots) {
      const ordered = [...members].sort((first, second) => first.start - second.start || first.end - second.end);
      const clusters: typeof ordered[] = [];
      let clusterEnd = -Infinity;
      for (const entry of ordered) {
        if (!clusters.length || entry.start >= clusterEnd) {
          clusters.push([entry]);
          clusterEnd = entry.end;
        } else {
          clusters[clusters.length - 1].push(entry);
          clusterEnd = Math.max(clusterEnd, entry.end);
        }
      }
      for (const cluster of clusters) {
        const laneEnds: number[] = [];
        const lanes = cluster.map((entry) => {
          let lane = laneEnds.findIndex((end) => end <= entry.start);
          if (lane < 0) lane = laneEnds.length;
          laneEnds[lane] = entry.end;
          return lane;
        });
        const laneCount = laneEnds.length;
        cluster.forEach((entry, index) => {
          const left = ((locationSlot + lanes[index] / laneCount) / locationCount) * 100;
          const right = (1 - (locationSlot + (lanes[index] + 1) / laneCount) / locationCount) * 100;
          entry.harness.style.setProperty("left", `calc(${left}% + 1px)`, "important");
          entry.harness.style.setProperty("right", `calc(${right}% + 1px)`, "important");
        });
      }
    }
  }
}

function leadingCrossPath(width: number, height: number, splitY: number) {
  const halfGap = 1.5;
  const armBottom = splitY - halfGap;
  const narrowRight = Math.min(CROSS_OVERLAP_STEM_WIDTH, width / 2 - halfGap);
  const eventBottom = height - halfGap;
  const radius = Math.max(3, Math.min(6, armBottom / 3, (eventBottom - armBottom) / 3, narrowRight / 8));
  const [w, r, arm, narrow, bottom] = [width, radius, armBottom, narrowRight, eventBottom].map(pathNumber);
  return `path("M ${r} 0 H ${pathNumber(w - r)} Q ${w} 0 ${w} ${r} V ${pathNumber(arm - r)} Q ${w} ${arm} ${pathNumber(w - r)} ${arm} H ${pathNumber(narrow + r)} Q ${narrow} ${arm} ${narrow} ${pathNumber(arm + r)} V ${pathNumber(bottom - r)} Q ${narrow} ${bottom} ${pathNumber(narrow - r)} ${bottom} H ${r} Q 0 ${bottom} 0 ${pathNumber(bottom - r)} V ${r} Q 0 0 ${r} 0 Z")`;
}

function trailingCrossPath(width: number, height: number, splitY: number) {
  const halfGap = 1.5;
  const top = halfGap;
  const fullTop = splitY + halfGap;
  const narrowLeft = Math.min(CROSS_OVERLAP_STEM_WIDTH, width / 2 - halfGap) + halfGap;
  const radius = Math.max(3, Math.min(6, splitY / 3, (height - fullTop) / 3, (width - narrowLeft) / 8));
  const [w, h, r, startTop, full, narrow] = [width, height, radius, top, fullTop, narrowLeft].map(pathNumber);
  return `path("M ${pathNumber(narrow + r)} ${startTop} H ${pathNumber(w - r)} Q ${w} ${startTop} ${w} ${pathNumber(startTop + r)} V ${pathNumber(h - r)} Q ${w} ${h} ${pathNumber(w - r)} ${h} H ${r} Q 0 ${h} 0 ${pathNumber(h - r)} V ${pathNumber(full + r)} Q 0 ${full} ${r} ${full} H ${pathNumber(narrow - r)} Q ${narrow} ${full} ${narrow} ${pathNumber(full - r)} V ${pathNumber(startTop + r)} Q ${narrow} ${startTop} ${pathNumber(narrow + r)} ${startTop} Z")`;
}

function trailingSharedEndPath(width: number, height: number) {
  const halfGap = 1.5;
  const top = halfGap;
  const left = Math.min(CROSS_OVERLAP_STEM_WIDTH, width / 2 - halfGap) + halfGap;
  const bottom = height - halfGap;
  const radius = Math.max(3, Math.min(6, (bottom - top) / 4, (width - left) / 8));
  const [w, r, startTop, startLeft, eventBottom] = [width, radius, top, left, bottom].map(pathNumber);
  return `path("M ${pathNumber(startLeft + r)} ${startTop} H ${pathNumber(w - r)} Q ${w} ${startTop} ${w} ${pathNumber(startTop + r)} V ${pathNumber(eventBottom - r)} Q ${w} ${eventBottom} ${pathNumber(w - r)} ${eventBottom} H ${pathNumber(startLeft + r)} Q ${startLeft} ${eventBottom} ${startLeft} ${pathNumber(eventBottom - r)} V ${pathNumber(startTop + r)} Q ${startLeft} ${startTop} ${pathNumber(startLeft + r)} ${startTop} Z")`;
}

function clearCrossOverlapVisual(entry: CrossOverlapEntry) {
  entry.harness.classList.remove("cross-overlap-harness");
  entry.harness.style.removeProperty("--cross-z-index");
  entry.eventElement.classList.remove("cross-overlap-leading", "cross-overlap-trailing", "cross-overlap-shared-end");
  entry.eventElement.style.removeProperty("clip-path");
  entry.eventElement.style.removeProperty("--cross-split-y");
  entry.eventElement.style.removeProperty("--cross-stem-width");
  entry.eventElement.removeAttribute("data-cross-overlap-role");
}

function applySideBySideFallback(entries: CrossOverlapEntry[]) {
  // Cross layout is applied with a removable CSS class. Clearing that class
  // reveals FullCalendar's own side-by-side positions without leaving stale
  // inline left/right values behind after dragging.
  for (const entry of entries) clearCrossOverlapVisual(entry);
}

function applyStaggeredOverlapLayout(root: HTMLElement | null, interactionActive = false) {
  if (!root) return;
  const containers = Array.from(root.querySelectorAll<HTMLElement>(".fc-timegrid-col-events"));
  for (const container of containers) {
    const entries = Array.from(container.children)
      .map((child) => {
        const harness = child as HTMLElement;
        if (!harness.classList.contains("fc-timegrid-event-harness")) return null;
        const eventElement = harness.querySelector<HTMLElement>(":scope > .fc-timegrid-event");
        const inner = eventElement?.querySelector<HTMLElement>(".event-inner[data-start-at][data-end-at]");
        if (!eventElement || !inner) return null;
        const start = new Date(inner.dataset.startAt || "").getTime();
        const end = new Date(inner.dataset.endAt || "").getTime();
        if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return null;
        return {
          harness,
          eventElement,
          inner,
          start,
          end,
          singleDay: localDayKey(start) === localDayKey(end - 1),
          wasCross: eventElement.classList.contains("cross-overlap-leading")
            || eventElement.classList.contains("cross-overlap-trailing")
        } satisfies CrossOverlapEntry;
      })
      .filter((entry): entry is CrossOverlapEntry => Boolean(entry));

    for (const entry of entries) clearCrossOverlapVisual(entry);
    if (interactionActive) continue;
    const ordered = [...entries].sort((first, second) => first.start - second.start || first.end - second.end);
    const clusters: CrossOverlapEntry[][] = [];
    let clusterEnd = -Infinity;
    for (const entry of ordered) {
      if (!clusters.length || entry.start >= clusterEnd) {
        clusters.push([entry]);
        clusterEnd = entry.end;
      } else {
        clusters[clusters.length - 1].push(entry);
        clusterEnd = Math.max(clusterEnd, entry.end);
      }
    }

    for (const cluster of clusters) {
      const [leading, trailing] = cluster;
      const isStaggeredPair = cluster.length === 2
        && leading.singleDay
        && trailing.singleDay
        && leading.start < trailing.start
        && trailing.start < leading.end
        && leading.end <= trailing.end;
      if (!isStaggeredPair) {
        if (cluster.some((entry) => entry.wasCross)) applySideBySideFallback(cluster);
        continue;
      }

      for (const [index, entry] of cluster.entries()) {
        entry.harness.classList.add("cross-overlap-harness");
        entry.harness.style.setProperty("--cross-z-index", String(index + 3));
      }
      const leadingRect = leading.eventElement.getBoundingClientRect();
      const trailingRect = trailing.eventElement.getBoundingClientRect();
      const leadingSplit = (trailing.start - leading.start) / (leading.end - leading.start) * leadingRect.height;
      const trailingSplit = (leading.end - trailing.start) / (trailing.end - trailing.start) * trailingRect.height;
      const sharesEnd = leading.end === trailing.end;
      // Compact cards still use the stepped layout as soon as each visible arm
      // can hold one real calendar row. Full details remain available through
      // the existing hover preview when the text itself does not fit.
      const segmentsAreLargeEnough = leadingSplit >= 20
        && leadingRect.height - leadingSplit >= 20
        && trailingSplit >= 20
        && (sharesEnd || trailingRect.height - trailingSplit >= 20);
      if (!segmentsAreLargeEnough || leadingRect.width < 72) {
        applySideBySideFallback(cluster);
        continue;
      }

      leading.eventElement.classList.add("cross-overlap-leading");
      trailing.eventElement.classList.add("cross-overlap-trailing");
      if (sharesEnd) trailing.eventElement.classList.add("cross-overlap-shared-end");
      leading.eventElement.dataset.crossOverlapRole = "leading";
      trailing.eventElement.dataset.crossOverlapRole = "trailing";
      leading.eventElement.style.setProperty("--cross-stem-width", `${CROSS_OVERLAP_STEM_WIDTH}px`);
      trailing.eventElement.style.setProperty("--cross-stem-width", `${CROSS_OVERLAP_STEM_WIDTH}px`);
      leading.eventElement.style.clipPath = leadingCrossPath(leadingRect.width, leadingRect.height, leadingSplit);
      trailing.eventElement.style.clipPath = sharesEnd
        ? trailingSharedEndPath(trailingRect.width, trailingRect.height)
        : trailingCrossPath(trailingRect.width, trailingRect.height, trailingSplit);
      leading.eventElement.style.setProperty("--cross-split-y", `${pathNumber(leadingSplit)}px`);
      trailing.eventElement.style.setProperty("--cross-split-y", `${pathNumber(trailingSplit)}px`);
    }
  }
}

function fitEventCardContent(root: HTMLElement | null) {
  if (!root) return;
  for (const inner of root.querySelectorAll<HTMLElement>(".event-inner")) {
    const primary = inner.querySelector<HTMLElement>(".event-card-primary");
    const details = inner.querySelector<HTMLElement>(".event-card-details");
    const content = [
      ...Array.from(primary?.children || []),
      ...Array.from(details?.children || [])
    ].filter((element): element is HTMLElement => element instanceof HTMLElement);
    content.forEach((element) => element.classList.remove("event-detail-partial-hidden"));
    inner.removeAttribute("data-content-clipped");

    const innerRect = inner.getBoundingClientRect();
    const bottomPadding = Number.parseFloat(getComputedStyle(inner).paddingBottom) || 0;
    const crossLeading = inner.closest<HTMLElement>(".cross-overlap-leading");
    const crossSplit = Number.parseFloat(crossLeading?.style.getPropertyValue("--cross-split-y") || "");
    // A leading card becomes a narrow time-continuation stem below the split.
    // Do not let text enter that indicator: even a complete DOM line would be
    // visually sliced by the clip-path.
    const fullWidthBottom = crossLeading && Number.isFinite(crossSplit)
      ? innerRect.top + crossSplit - 5
      : innerRect.bottom - bottomPadding;
    const visibleBottom = Math.min(innerRect.bottom - bottomPadding, fullWidthBottom);
    let hideFollowing = false;
    for (const element of Array.from(primary?.children || []).filter((item): item is HTMLElement => item instanceof HTMLElement)) {
      const rect = element.getBoundingClientRect();
      if (hideFollowing || rect.top < innerRect.top - 0.5 || rect.bottom > visibleBottom + 0.5) {
        element.classList.add("event-detail-partial-hidden");
        hideFollowing = true;
        inner.dataset.contentClipped = "true";
      }
    }

    if (!details) continue;
    const detailsRect = details.getBoundingClientRect();
    const detailsVisibleBottom = Math.min(detailsRect.bottom, visibleBottom);
    hideFollowing = false;
    for (const element of Array.from(details.children).filter((item): item is HTMLElement => item instanceof HTMLElement)) {
      const rect = element.getBoundingClientRect();
      if (hideFollowing || rect.top < detailsRect.top - 0.5 || rect.bottom > detailsVisibleBottom + 0.5) {
        element.classList.add("event-detail-partial-hidden");
        hideFollowing = true;
        inner.dataset.contentClipped = "true";
      }
    }
  }
}

function WorkTypePicker({ value, bootstrap, disabled = false, compact = false, onChange }: {
  value: string[];
  bootstrap: Bootstrap;
  disabled?: boolean;
  compact?: boolean;
  onChange: (value: string[]) => void;
}) {
  const selected = value.length ? value : [bootstrap.workTypes[0]];
  function toggle(type: string) {
    const next = selected.includes(type) ? selected.filter((item) => item !== type) : [...selected, type];
    if (next.length) onChange(bootstrap.workTypes.filter((item) => next.includes(item)));
  }
  return (
    <div className={`work-type-picker ${compact ? "compact" : ""}`} role="group" aria-label="工作类型（可多选）">
      {bootstrap.workTypes.map((type) => (
        <label className={selected.includes(type) ? "selected" : ""} key={type}>
          <input type="checkbox" disabled={disabled} checked={selected.includes(type)} onChange={() => toggle(type)} />
          <i style={{ background: bootstrap.workTypeColors[type] }} />
          <span>{type}</span>
        </label>
      ))}
    </div>
  );
}

function ParticipantPicker({ value, employeeId, bootstrap, disabled = false, compact = false, onChange, assignments = {}, onAssignmentsChange }: {
  value: number[];
  assignments?: Record<number, string[]>;
  onAssignmentsChange?: (value: Record<number, string[]>) => void;
  employeeId: number | null;
  bootstrap: Bootstrap;
  disabled?: boolean;
  compact?: boolean;
  onChange: (value: number[]) => void;
}) {
  const availableUsers = bootstrap.users.filter((user) => user.active && user.id !== employeeId);
  function toggle(userId: number) {
    const next = value.includes(userId) ? value.filter((id) => id !== userId) : [...value, userId];
    onChange(availableUsers.filter((user) => next.includes(user.id)).map((user) => user.id));
  }
  return (
    <div className="participant-work-editor">
    <div className={`participant-picker ${compact ? "compact" : ""}`} role="group" aria-label="参与人员（可多选）">
      {availableUsers.map((user) => (
        <label className={value.includes(user.id) ? "selected" : ""} key={user.id}>
          <input type="checkbox" disabled={disabled} checked={value.includes(user.id)} onChange={() => toggle(user.id)} />
          <span>{user.displayName}</span>
        </label>
      ))}
      {!availableUsers.length && <small>暂无其他可选员工</small>}
    </div>
    {availableUsers.filter((user) => value.includes(user.id)).map((user) => (
      <fieldset className="participant-work-row" key={user.id} disabled={disabled}>
        <legend>{user.displayName} · 参与工作（可多选）</legend>
        <div className={`work-type-picker ${compact ? "compact" : ""}`} role="group" aria-label={`${user.displayName}参与工作类型（可多选）`}>
          {bootstrap.workTypes.map((type) => {
            const selected = assignments[user.id] || [];
            return <label key={type} className={selected.includes(type) ? "selected" : ""}>
              <input type="checkbox" checked={selected.includes(type)} onChange={() => onAssignmentsChange?.({ ...assignments,
                [user.id]: bootstrap.workTypes.filter((item) => item === type ? !selected.includes(type) : selected.includes(item))
              })} />
              <i style={{ background: bootstrap.workTypeColors[type] }} /><span>{type}</span>
            </label>;
          })}
        </div>
        {!(assignments[user.id]?.length) && <small>请选择参与工作类型，选择后按该员工原有规则计提。</small>}
      </fieldset>
    ))}
    </div>
  );
}

function ProjectPicker({ value, bootstrap, disabled = false, listId, onChange }: {
  value: string;
  bootstrap: Bootstrap;
  disabled?: boolean;
  listId: string;
  onChange: (value: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const summary = projectSummaryForName(bootstrap, value);
  const search = value.trim().toLocaleLowerCase("zh-CN");
  const options = (bootstrap.projectNames || []).filter((name) => !search || name.toLocaleLowerCase("zh-CN").includes(search));
  return (
    <div className="project-picker" onBlur={(event) => {
      if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setOpen(false);
    }}>
      <div className="project-picker-control">
        <input
          id={listId}
          disabled={disabled}
          value={value}
          onFocus={() => setOpen(true)}
          onChange={(event) => { onChange(event.target.value); setOpen(true); }}
          onKeyDown={(event) => {
            if (event.key === "Escape") setOpen(false);
            if (event.key === "ArrowDown" && !open) setOpen(true);
          }}
          role="combobox"
          aria-expanded={open}
          aria-controls={`${listId}-options`}
          aria-autocomplete="list"
          placeholder="选择已有项目或输入新项目"
          autoComplete="off"
        />
        <button type="button" disabled={disabled} aria-label="显示已有项目" aria-expanded={open} onMouseDown={(event) => event.preventDefault()} onClick={() => setOpen((current) => !current)}>
          <ChevronDown size={15} />
        </button>
      </div>
      {open && !disabled && (
        <div className="project-picker-options" id={`${listId}-options`} role="listbox">
          {options.map((name) => (
            <button type="button" role="option" aria-selected={name === value} key={name} onMouseDown={(event) => event.preventDefault()} onClick={() => { onChange(name); setOpen(false); }}>
              <span>{name}</span>
              {name === value && <Check size={14} />}
            </button>
          ))}
          {!options.length && <span className="project-picker-empty">没有匹配项目，可直接输入新名称</span>}
        </div>
      )}
      <small className="field-hint">可选择已有项目，也可直接输入新项目名称</small>
      {summary && (
        <small className="project-total-hint">
          {summary.count} 条日程 · {formatHours(summary.totalMinutes)} · {summary.pricingMode === "quantity" ? "按数量" : "按时间"} · {summary.pricingMode === "quantity" ? "每个日程" : "小时"}单价 {moneyText(summary.unitPrice)} · 预估 {moneyText(summary.estimatedTotal)} · 实收 {moneyText(summary.totalAmount)}
        </small>
      )}
    </div>
  );
}

function RecentChangeSidebar({ feed }: { feed: NotificationFeed }) {
  const actionLabel = (action: RecentEventChange["action"]) => action === "create" ? "新建了" : action === "delete" ? "删除了" : "更新了";
  return (
    <section className="sidebar-recent-changes" aria-label="最近日程变更">
      <header>
        <div><Bell size={14} /><strong>最近日程变更</strong></div>
        <small>{feed.retentionDays} 天内 · 无需确认</small>
      </header>
      <div className="sidebar-recent-list">
        {feed.recentChanges.slice(0, 10).map((change) => (
          <article key={change.id}>
            <i className={`change-dot change-${change.action}`} />
            <div>
              <strong>{change.actorName}{actionLabel(change.action)}「{change.title}」</strong>
              <span>
                {change.startAt && change.endAt ? compactTimeRange(change.startAt, change.endAt) : "日程时间已移除"}
                {change.clientProject ? ` · ${change.clientProject}` : ""} · {relativeTimeText(change.createdAt)}
              </span>
            </div>
          </article>
        ))}
        {feed.recentChanges.length === 0 && <span className="recent-change-empty">最近 {feed.retentionDays} 天暂无日程变更</span>}
      </div>
    </section>
  );
}

function ActionNotifications({ feed, acknowledgingEventId, onAcknowledge }: {
  feed: NotificationFeed;
  acknowledgingEventId: number | null;
  onAcknowledge: (eventId: number) => Promise<void>;
}) {
  const total = feed.reviewTasks.length + feed.pendingAssignments.length;
  if (total === 0) return null;
  return (
    <section className={`assignment-notifications assignment-count-${Math.min(total, 3)}`} role="alert" aria-label={`${total} 条事项待处理`}>
      <header>
        <AlertTriangle size={19} />
        <div>
          <strong>{total} 条事项待处理</strong>
          <small>完成对应工作后自动消失</small>
        </div>
      </header>
      <div className="assignment-alert-list">
        {feed.reviewTasks.map((item) => (
          <article className="review-task-alert" key={item.id}>
            <div>
              <strong>{item.title}</strong>
              <span>{item.body}</span>
            </div>
            <a href={item.actionUrl} target="_blank" rel="noreferrer">
              <AudioLines size={15} />{item.actionLabel}
            </a>
          </article>
        ))}
        {feed.pendingAssignments.map((item) => (
          <article key={item.eventId}>
            <div>
              <strong>{item.title}</strong>
              <span>{compactTimeRange(item.startAt, item.endAt)}{item.clientProject ? ` · ${item.clientProject}` : ""} · {item.assignedByName} 委派</span>
            </div>
            <button type="button" disabled={acknowledgingEventId === item.eventId} onClick={() => void onAcknowledge(item.eventId)}>
              <Check size={15} />{acknowledgingEventId === item.eventId ? "确认中…" : "确认收到"}
            </button>
          </article>
        ))}
      </div>
    </section>
  );
}

function Login({ onSuccess }: { onSuccess: () => Promise<void> }) {
  const [username, setUsername] = useState("admin");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setLoading(true);
    setError("");
    try {
      await request("/api/login", { method: "POST", body: JSON.stringify({ username, password, remember }) });
      await onSuccess();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "登录失败");
    } finally {
      setLoading(false);
    }
  }

  return (
    <main className="login-shell">
      <section className="login-card">
        <div className="login-mark"><CalendarDays size={30} strokeWidth={1.8} /></div>
        <div className="login-heading">
          <p className="eyebrow">ALE INTERNAL</p>
          <h1>{new URLSearchParams(window.location.search).has("sso") ? "员工统一登录" : "工作日历"}</h1>
          <p>工作日历 · 音频审阅 · 同一套员工账号</p>
        </div>
        <form onSubmit={submit} className="login-form">
          <label>
            <span>用户名</span>
            <input autoFocus autoComplete="username" value={username} onChange={(event) => setUsername(event.target.value)} placeholder="请输入用户名" />
          </label>
          <label>
            <span>密码</span>
            <input type="password" autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} placeholder="请输入密码" />
          </label>
          <label className="remember-row">
            <input type="checkbox" checked={remember} onChange={(event) => setRemember(event.target.checked)} />
            <span>在此设备保持登录</span>
          </label>
          {error && <div className="form-error">{error}</div>}
          <button className="primary-button login-button" disabled={loading || !username || !password}>
            {loading ? "正在登录…" : "登录"}
          </button>
        </form>
        <p className="login-footnote">仅限已授权人员使用</p>
      </section>
    </main>
  );
}

function EventModal({
  value, bootstrap, readOnly, onClose, onSave, onDelete, onExportProjectSchedule
}: {
  value: EventRecord;
  bootstrap: Bootstrap;
  readOnly: boolean;
  onClose: () => void;
  onSave: (value: EventRecord) => Promise<void>;
  onDelete: ((id: number) => Promise<void>) | null;
  onExportProjectSchedule: ((project: string) => void) | null;
}) {
  const [form, setForm] = useState(value);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const set = <K extends keyof EventRecord>(key: K, next: EventRecord[K]) => setForm((current) => ({ ...current, [key]: next }));
  const projectEstimatedTotal = projectedProjectTotal(value, form, bootstrap);
  const collaboratorRestricted = Boolean(value.collaborationRootEventId && bootstrap.user.role === "staff");
  const canFillEstimatedPrice = bootstrap.user.canEditEstimatedPrice && Boolean(form.clientProject.trim());

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      await onSave({ ...form, startAt: toIso(form.startAt), endAt: toIso(form.endAt) });
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "保存失败");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className="modal-card event-modal" role="dialog" aria-modal="true" aria-label={value.id ? "编辑日程" : "新建日程"}>
        <header className="modal-header">
          <div>
            <p className="eyebrow">{value.id ? "EVENT DETAILS" : "NEW EVENT"}</p>
            <h2>{value.id ? "编辑日程" : "新建日程"}</h2>
          </div>
          <button className="icon-button" onClick={onClose} aria-label="关闭"><X size={19} /></button>
        </header>
        <form onSubmit={submit} className="event-form">
          <label className="field field-wide">
            <span>日程标题</span>
            <input required disabled={readOnly || collaboratorRestricted} value={form.title} onChange={(event) => set("title", event.target.value)} placeholder="例如：品牌宣传片人声录制" />
          </label>
          <label className="field">
            <span>开始时间</span>
            <input type="datetime-local" required disabled={readOnly} value={localDateTime(form.startAt)} onChange={(event) => set("startAt", event.target.value)} />
          </label>
          <label className="field">
            <span>结束时间</span>
            <input type="datetime-local" required disabled={readOnly} value={localDateTime(form.endAt)} onChange={(event) => set("endAt", event.target.value)} />
          </label>
          <div className="field field-wide">
            <span>工作类型（可多选）</span>
            <WorkTypePicker
              value={eventWorkTypes(form)}
              bootstrap={bootstrap}
              disabled={readOnly}
              onChange={(types) => setForm((current) => ({ ...current, workTypes: types, workType: types[0] }))}
            />
          </div>
          <label className="field">
            <span>状态</span>
            <select disabled={readOnly || collaboratorRestricted} value={form.status} onChange={(event) => set("status", event.target.value as EventRecord["status"])}>
              <option value="scheduled">已安排</option>
              <option value="completed">已完成</option>
              <option value="cancelled">已取消</option>
            </select>
          </label>
          <label className="field">
            <span>负责人</span>
            <select disabled={readOnly || bootstrap.user.role === "staff"} value={form.employeeId ?? ""} onChange={(event) => {
              const employeeId = event.target.value ? Number(event.target.value) : null;
              setForm((current) => ({ ...current, employeeId, participantIds: current.participantIds.filter((id) => id !== employeeId) }));
            }}>
              <option value="">未指定负责人</option>
              {bootstrap.users.filter((user) => user.active).map((user) => <option value={user.id} key={user.id}>{user.displayName}</option>)}
            </select>
          </label>
          <div className="field field-wide">
            <span>参与人员（可多选）</span>
            <ParticipantPicker value={form.participantIds} assignments={form.participantWorkTypes} onAssignmentsChange={(participantWorkTypes) => set("participantWorkTypes", participantWorkTypes)} employeeId={form.employeeId} bootstrap={bootstrap} disabled={readOnly || collaboratorRestricted} onChange={(participantIds) => set("participantIds", participantIds)} />
          </div>
          <label className="field">
            <span>项目</span>
            <ProjectPicker value={form.clientProject} bootstrap={bootstrap} disabled={readOnly || collaboratorRestricted} listId="event-project-options" onChange={(next) => setForm((current) => projectFormChange(current, next, bootstrap))} />
          </label>
          <label className="field">
            <span>店铺</span>
            <select disabled={readOnly || collaboratorRestricted} value={form.storeName} onChange={(event) => set("storeName", event.target.value)}>
              <option value="">未选择店铺</option>
              {bootstrap.storeNames.map((name) => <option value={name} key={name}>{name}</option>)}
            </select>
          </label>
          {form.clientProject.trim() && (
            <label className="field">
              <span>项目计价方式</span>
              <select disabled={readOnly || collaboratorRestricted} value={form.pricingMode} onChange={(event) => set("pricingMode", event.target.value as PricingMode)}>
                <option value="time">按时间</option>
                <option value="quantity">按数量</option>
              </select>
              <small className="field-hint">按数量时，每条有效日程计一次；协作流程不重复计数</small>
            </label>
          )}
          <label className="field">
            <span>{form.clientProject.trim() ? (form.pricingMode === "quantity" ? "项目每个日程单价" : "项目小时单价") : "小时单价"}</span>
            <input type="number" min="0" step="0.01" disabled={readOnly || collaboratorRestricted} value={form.unitPrice ?? ""} onChange={(event) => set("unitPrice", event.target.value === "" ? null : Number(event.target.value))} placeholder="¥ 0.00" />
            {form.clientProject.trim() && <small className="field-hint">{form.pricingMode === "quantity" ? "每个日程的价格" : "每小时价格"}，项目内所有日程共享</small>}
          </label>
          <label className="field">
            <span>{canFillEstimatedPrice ? "项目预估总价（可填写）" : form.clientProject.trim() ? "项目预估总价" : "预估总价"}</span>
            <input
              type={canFillEstimatedPrice ? "number" : "text"}
              min={canFillEstimatedPrice ? "0" : undefined}
              step={canFillEstimatedPrice ? "0.01" : undefined}
              readOnly={!canFillEstimatedPrice}
              value={canFillEstimatedPrice ? form.projectEstimatedTotalOverride ?? "" : projectEstimatedTotal == null ? "" : projectEstimatedTotal.toFixed(2)}
              onChange={canFillEstimatedPrice ? (event) => set("projectEstimatedTotalOverride", event.target.value === "" ? null : Number(event.target.value)) : undefined}
              placeholder={canFillEstimatedPrice && projectEstimatedTotal != null ? `留空自动计算：${projectEstimatedTotal.toFixed(2)}` : "填写项目和单价后自动计算"}
            />
            {form.clientProject.trim() && <small className="field-hint">{canFillEstimatedPrice ? `填写后覆盖自动金额；留空恢复${form.pricingMode === "quantity" ? "每个日程单价 × 有效日程数量" : "项目小时单价 × 未取消日程总工时"}` : form.pricingMode === "quantity" ? "每个日程单价 × 有效日程数量" : "项目小时单价 × 未取消日程总工时"}</small>}
          </label>
          <label className="field">
            <span>{form.clientProject.trim() ? "项目实收总金额" : "实收总金额"}</span>
            <input type="number" min="0" step="0.01" disabled={readOnly || collaboratorRestricted} value={form.totalPrice ?? ""} onChange={(event) => set("totalPrice", event.target.value === "" ? null : Number(event.target.value))} placeholder="¥ 0.00" />
            {form.clientProject.trim() && <small className="field-hint">{collaboratorRestricted ? "协作流程共享主项目金额，不可修改" : "实际收费，人工填写；项目内共享且后台只统计一次"}</small>}
          </label>
          <label className="field field-wide">
            <span>备注</span>
            <textarea disabled={readOnly || collaboratorRestricted} value={form.notes} onChange={(event) => set("notes", event.target.value)} placeholder="设备、联系人、准备事项等" rows={3} />
          </label>
          {error && <div className="form-error field-wide">{error}</div>}
          <footer className="modal-actions field-wide">
            <div className="modal-actions-left">
              {onDelete && !readOnly && value.id && (
                <button type="button" className="danger-button" onClick={() => onDelete(value.id!)}><Trash2 size={16} />删除</button>
              )}
              {onExportProjectSchedule && value.id && value.clientProject.trim() && (
                <button type="button" className="secondary-button project-export-button" onClick={() => onExportProjectSchedule(value.clientProject.trim())}><FileSpreadsheet size={16} />导出项目时间表</button>
              )}
            </div>
            <div className="modal-actions-right">
              <button type="button" className="secondary-button" onClick={onClose}>{readOnly ? "关闭" : "取消"}</button>
              {(!readOnly || canFillEstimatedPrice) && <button className="primary-button" disabled={saving}>{saving ? "保存中…" : "保存日程"}</button>}
            </div>
          </footer>
        </form>
      </section>
    </div>
  );
}

function InlineEventEditor({
  value, bootstrap, style, onCancel, onSave
}: {
  value: EventRecord;
  bootstrap: Bootstrap;
  style: React.CSSProperties;
  onCancel: () => void;
  onSave: (value: EventRecord) => Promise<void>;
}) {
  const [form, setForm] = useState(value);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const set = <K extends keyof EventRecord>(key: K, next: EventRecord[K]) => setForm((current) => ({ ...current, [key]: next }));
  const projectEstimatedTotal = projectedProjectTotal(value, form, bootstrap);
  const canFillEstimatedPrice = bootstrap.user.canEditEstimatedPrice && Boolean(form.clientProject.trim());

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!form.title.trim()) return;
    setSaving(true);
    setError("");
    try {
      await onSave({ ...form, title: form.title.trim() });
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "保存失败");
    } finally {
      setSaving(false);
    }
  }

  return (
    <form
      className="inline-event-editor"
      style={{ ...style, borderColor: bootstrap.workTypeColors[eventWorkTypes(form)[0]] }}
      onSubmit={submit}
      onMouseDown={(event) => event.stopPropagation()}
      onKeyDown={(event) => { if (event.key === "Escape") onCancel(); }}
      aria-label="在选取时间内新建日程"
    >
      <div className="inline-editor-accent" style={{ background: workTypeBackground(eventWorkTypes(form), bootstrap) }} />
      <header className="inline-editor-header">
        <span>{compactTimeRange(form.startAt, form.endAt)}</span>
        <button type="button" onClick={onCancel} aria-label="取消新建日程"><X size={15} /></button>
      </header>
      <input
        className="inline-title-input"
        autoFocus
        required
        value={form.title}
        onChange={(event) => set("title", event.target.value)}
        placeholder="输入日程标题"
      />
      <div className="inline-editor-grid">
        <div className="inline-work-types">
          <WorkTypePicker
            compact
            value={eventWorkTypes(form)}
            bootstrap={bootstrap}
            onChange={(types) => setForm((current) => ({ ...current, workTypes: types, workType: types[0] }))}
          />
        </div>
        <select aria-label="负责人" disabled={bootstrap.user.role === "staff"} value={form.employeeId ?? ""} onChange={(event) => {
          const employeeId = event.target.value ? Number(event.target.value) : null;
          setForm((current) => ({ ...current, employeeId, participantIds: current.participantIds.filter((id) => id !== employeeId) }));
        }}>
          <option value="">未指定负责人</option>
          {bootstrap.users.filter((user) => user.active).map((user) => <option value={user.id} key={user.id}>{user.displayName}</option>)}
        </select>
        <div className="inline-participants"><span>参与人员</span><ParticipantPicker compact value={form.participantIds} assignments={form.participantWorkTypes} onAssignmentsChange={(participantWorkTypes) => set("participantWorkTypes", participantWorkTypes)} employeeId={form.employeeId} bootstrap={bootstrap} onChange={(participantIds) => set("participantIds", participantIds)} /></div>
        <ProjectPicker value={form.clientProject} bootstrap={bootstrap} listId="inline-project-options" onChange={(next) => setForm((current) => projectFormChange(current, next, bootstrap))} />
        <select aria-label="店铺" value={form.storeName} onChange={(event) => set("storeName", event.target.value)}>
          <option value="">未选择店铺</option>
          {bootstrap.storeNames.map((name) => <option value={name} key={name}>{name}</option>)}
        </select>
        {form.clientProject.trim() && <select aria-label="项目计价方式" value={form.pricingMode} onChange={(event) => set("pricingMode", event.target.value as PricingMode)}><option value="time">按时间</option><option value="quantity">按数量</option></select>}
        <input aria-label={form.pricingMode === "quantity" ? "项目每个日程单价" : "项目小时单价"} type="number" min="0" step="0.01" value={form.unitPrice ?? ""} onChange={(event) => set("unitPrice", event.target.value === "" ? null : Number(event.target.value))} placeholder={form.pricingMode === "quantity" ? "每个日程单价（元）" : "项目小时单价（元/小时）"} />
        <input
          aria-label="项目预估总价"
          type={canFillEstimatedPrice ? "number" : "text"}
          min={canFillEstimatedPrice ? "0" : undefined}
          step={canFillEstimatedPrice ? "0.01" : undefined}
          readOnly={!canFillEstimatedPrice}
          value={canFillEstimatedPrice ? form.projectEstimatedTotalOverride ?? "" : projectEstimatedTotal == null ? "" : projectEstimatedTotal.toFixed(2)}
          onChange={canFillEstimatedPrice ? (event) => set("projectEstimatedTotalOverride", event.target.value === "" ? null : Number(event.target.value)) : undefined}
          placeholder={canFillEstimatedPrice && projectEstimatedTotal != null ? `留空自动：${projectEstimatedTotal.toFixed(2)}` : "项目预估总价（自动）"}
        />
        <input aria-label="项目实收总金额" type="number" min="0" step="0.01" value={form.totalPrice ?? ""} onChange={(event) => set("totalPrice", event.target.value === "" ? null : Number(event.target.value))} placeholder="项目实收总金额（元）" />
        <select aria-label="状态" value={form.status} onChange={(event) => set("status", event.target.value as EventRecord["status"])}>
          <option value="scheduled">已安排</option><option value="completed">已完成</option><option value="cancelled">已取消</option>
        </select>
        <textarea className="inline-notes" value={form.notes} onChange={(event) => set("notes", event.target.value)} placeholder="备注" rows={3} />
      </div>
      {error && <div className="inline-editor-error">{error}</div>}
      <footer className="inline-editor-actions">
        <span>所有信息保存后会显示在日程卡片上</span>
        <button className="inline-save-button" disabled={saving || !form.title.trim()}>{saving ? "保存中…" : "保存"}</button>
      </footer>
    </form>
  );
}

function EventCardDetails({ value, bootstrap }: { value: EventRecord; bootstrap: Bootstrap }) {
  const employee = bootstrap.users.find((item) => item.id === value.employeeId)?.displayName || "未指定负责人";
  const participants = value.participantIds.map((id) => `${bootstrap.users.find((item) => item.id === id)?.displayName || `用户${id}`}（${value.participantWorkTypes?.[id]?.join("、") || "未选工作类型"}）`);
  const selectedWorkTypes = eventWorkTypes(value);
  return (
    <div className="event-card-details">
      {value.isCollaborative && <small>协作流程 · 工时与金额不重复统计</small>}
      <small>类型：{selectedWorkTypes.join("、")}</small>
      <small>负责人：{employee}</small>
      {participants.length > 0 && <small>参与人员：{participants.join("、")}</small>}
      {value.clientProject && <small>项目：{value.clientProject}</small>}
      {value.storeName && <small>店铺：{value.storeName}</small>}
      {value.clientProject && <small>计价方式：{value.pricingMode === "quantity" ? "按数量" : "按时间"}</small>}
      {value.unitPrice != null && <small>{value.clientProject ? (value.pricingMode === "quantity" ? "项目每个日程单价" : "项目小时单价") : "小时单价"}：{moneyText(value.unitPrice)}{value.pricingMode === "quantity" && value.clientProject ? "/条" : "/小时"}</small>}
      {value.clientProject && value.projectEstimatedTotal != null && <small>项目预估总价：{moneyText(value.projectEstimatedTotal)}</small>}
      {value.totalPrice != null && <small>{value.clientProject ? "项目实收总金额" : "实收总金额"}：{moneyText(value.totalPrice)}</small>}
      {value.notes && <small className="event-card-notes">备注：{value.notes}</small>}
    </div>
  );
}

function EventCard({
  value, timeText, bootstrap, onHover, onLeave, onAddOverlap, onCollaboratePointerDown, onCopyPointerDown, onExportProjectSchedule, onDelete
}: {
  value: EventRecord;
  timeText: string;
  bootstrap: Bootstrap;
  onHover: (element: HTMLDivElement) => void;
  onLeave: () => void;
  onAddOverlap?: () => void;
  onCollaboratePointerDown?: (event: React.PointerEvent<HTMLButtonElement>) => void;
  onCopyPointerDown?: (event: React.PointerEvent<HTMLButtonElement>) => void;
  onExportProjectSchedule?: () => void;
  onDelete?: () => void;
}) {
  const selectedWorkTypes = eventWorkTypes(value);

  return (
    <div
      className="event-inner"
      data-event-id={value.id}
      data-collaboration-group-id={value.collaborationRootEventId ?? value.id}
      data-start-at={value.startAt}
      data-end-at={value.endAt}
      data-store-name={value.storeName}
      style={{ background: workTypeBackground(selectedWorkTypes, bootstrap), borderLeftColor: storeMarkerColor(value.storeName, bootstrap) }}
      onMouseEnter={(event) => onHover(event.currentTarget)}
      onMouseLeave={onLeave}
    >
      <i className="event-cross-store-stripe" aria-hidden="true" style={{ background: storeMarkerColor(value.storeName, bootstrap) }} />
      <div className="event-card-primary">
        <strong>{value.title}</strong>
        <span className="event-card-time">◷ {timeText}</span>
      </div>
      <EventCardDetails value={value} bootstrap={bootstrap} />
      {onAddOverlap && (
        <button
          type="button"
          className="event-card-action event-add-overlap-button"
          aria-label="新增同时间日程"
          title="在同一时间新增日程"
          onPointerDown={(event) => event.stopPropagation()}
          onClick={(event) => { event.preventDefault(); event.stopPropagation(); onAddOverlap(); }}
        ><Plus size={12} strokeWidth={2.5} /></button>
      )}
      {onCopyPointerDown && (
        <button
          type="button"
          className="event-card-action event-copy-handle"
          aria-label="拖动复制日程"
          title="按住并拖动复制"
          onPointerDown={onCopyPointerDown}
          onClick={(event) => { event.preventDefault(); event.stopPropagation(); }}
        ><Copy size={11} strokeWidth={2.3} /></button>
      )}
      {onCollaboratePointerDown && (
        <button
          type="button"
          className="event-card-action event-collaborate-handle"
          aria-label="参与项目协作"
          title="参与项目协作：拖到你的工作时间后松开"
          onPointerDown={onCollaboratePointerDown}
          onClick={(event) => { event.preventDefault(); event.stopPropagation(); }}
        ><Handshake size={11} strokeWidth={2.3} /></button>
      )}
      {onExportProjectSchedule && (
        <button
          type="button"
          className="event-card-action event-schedule-export-button"
          aria-label={`导出${value.clientProject}项目时间表`}
          title={`导出“${value.clientProject}”项目全部日程`}
          onPointerDown={(event) => event.stopPropagation()}
          onClick={(event) => { event.preventDefault(); event.stopPropagation(); onExportProjectSchedule(); }}
        ><FileSpreadsheet size={11} strokeWidth={2.3} /><span>时间表</span></button>
      )}
      {onDelete && (
        <button
          type="button"
          className="event-card-action event-delete-button"
          aria-label="删除日程"
          title="删除日程"
          onPointerDown={(event) => event.stopPropagation()}
          onClick={(event) => { event.preventDefault(); event.stopPropagation(); onDelete(); }}
        ><X size={12} strokeWidth={2.5} /></button>
      )}
    </div>
  );
}

function EventHoverPreview({ preview, bootstrap }: { preview: EventHoverPreviewState; bootstrap: Bootstrap }) {
  const selectedWorkTypes = eventWorkTypes(preview.value);
  return (
    <aside
      className="event-hover-preview"
      aria-hidden="true"
      style={{ ...preview.style, background: workTypeTranslucentBackground(selectedWorkTypes, bootstrap), borderLeftColor: storeMarkerColor(preview.value.storeName, bootstrap) }}
    >
      <div className="event-hover-label">展开信息 · 原卡片高度为实际时长</div>
      <div className="event-card-primary">
        <strong>{preview.value.title}</strong>
        <span className="event-card-time">◷ {preview.timeText}</span>
      </div>
      <EventCardDetails value={preview.value} bootstrap={bootstrap} />
    </aside>
  );
}

function PayrollPanel({ bootstrap, query, employeeId }: { bootstrap: Bootstrap; query: string; employeeId: string }) {
  const [calculation, setCalculation] = useState<PayrollCalculation | null>(null);
  const [settingsForm, setSettingsForm] = useState<{ baseSalary: string; employeeSocialSecurityContribution: string; commissionRates: Record<string, string> } | null>(null);
  const [drafts, setDrafts] = useState<Record<number, { amount: string; rate: string }>>({});
  const [loading, setLoading] = useState(false);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [savingRow, setSavingRow] = useState<number | null>(null);
  const [savingHoliday, setSavingHoliday] = useState<string | null>(null);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const load = useCallback(async () => {
    if (!employeeId) {
      setCalculation(null);
      setSettingsForm(null);
      setDrafts({});
      return;
    }
    setLoading(true);
    setError("");
    try {
      const data = await request<PayrollCalculation>(`/api/payroll/calculate?${query}`);
      setCalculation(data);
      setSettingsForm({
        baseSalary: String(data.settings.baseSalary),
        employeeSocialSecurityContribution: String(data.settings.employeeSocialSecurityContribution),
        commissionRates: Object.fromEntries(bootstrap.workTypes.map((type) => [type, String(data.settings.commissionRates[type] ?? 0)]))
      });
      setDrafts(Object.fromEntries(data.rows.map((row) => [row.id, {
        amount: row.actualCalculationAmount == null ? "" : String(row.actualCalculationAmount),
        rate: row.actualCalculationRate == null ? "" : String(row.actualCalculationRate)
      }])));
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "工资计算失败");
    } finally {
      setLoading(false);
    }
  }, [bootstrap.workTypes, employeeId, query]);

  useEffect(() => { void load(); }, [load]);

  async function saveSettings(event: React.FormEvent) {
    event.preventDefault();
    if (!settingsForm || !employeeId) return;
    setSettingsSaving(true);
    setError("");
    setMessage("");
    try {
      await request(`/api/payroll/settings/${employeeId}`, {
        method: "PATCH",
        body: JSON.stringify({
          baseSalary: settingsForm.baseSalary,
          employeeSocialSecurityContribution: settingsForm.employeeSocialSecurityContribution,
          commissionRates: settingsForm.commissionRates
        })
      }, bootstrap.csrfToken);
      setMessage("工资规则已保存，并已重新计算");
      await load();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "工资规则保存失败");
    } finally {
      setSettingsSaving(false);
    }
  }

  async function markHoliday(date: string, enabled: boolean) {
    if (!employeeId || savingHoliday || loading) return;
    setSavingHoliday(date);
    setError("");
    setMessage("");
    try {
      await request(`/api/payroll/holiday-overtime/${employeeId}/${date}`, {
        method: "PATCH", body: JSON.stringify({ enabled })
      }, bootstrap.csrfToken);
      await load();
      setMessage(enabled ? `${date} 已标记加班，并已重新计算工资` : `${date} 已取消加班标记，并已重新计算工资`);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "加班标记保存失败");
    } finally { setSavingHoliday(null); }
  }

  async function saveOverride(rowId: number, reset = false) {
    const draft = drafts[rowId] || { amount: "", rate: "" };
    setSavingRow(rowId);
    setError("");
    setMessage("");
    try {
      await request(`/api/events/${rowId}/payroll`, {
        method: "PATCH",
        body: JSON.stringify({
          employeeId,
          actualCalculationAmount: reset || draft.amount === "" ? null : draft.amount,
          actualCalculationRate: reset || draft.rate === "" ? null : draft.rate
        })
      }, bootstrap.csrfToken);
      setMessage(reset ? "已恢复该记录的默认计算规则" : "实际计算项已保存");
      await load();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "实际计算项保存失败");
    } finally {
      setSavingRow(null);
    }
  }

  if (!employeeId) {
    return (
      <section className="payroll-panel payroll-empty no-print">
        <ShieldCheck size={21} />
        <div><strong>管理员工资计算</strong><p>请先在上方“员工”中选择一名员工。工资内容不会向员工账号显示。</p></div>
      </section>
    );
  }

  return (
    <section className="payroll-panel" aria-label="管理员工资计算台">
      <header className="payroll-heading">
        <div>
          <span className="admin-only-badge"><ShieldCheck size={13} />仅管理员可见</span>
          <h3><Calculator size={19} />工资计算 · {calculation?.settings.employee.displayName || "载入中"}</h3>
          <p>默认金额取项目实收总金额，同一项目在本次计算中只计一次；负责人和参与人员分别按各自所选工作类型与原有比例计提，多选比例相加。未选参与工作类型的人员不计提。实际计算金额或比例可分别覆盖默认值。已取消日程不计入工资。</p>
        </div>
        <button className="secondary-button no-print" onClick={() => void load()} disabled={loading}><RefreshCw size={15} />重新计算</button>
      </header>

      {error && <div className="form-error payroll-feedback">{error}</div>}
      {message && <div className="payroll-success payroll-feedback"><Check size={14} />{message}</div>}

      {settingsForm && calculation && (
        <form className="payroll-rules no-print" onSubmit={saveSettings}>
          <div className="payroll-rule-intro"><strong>员工工资规则</strong><small>底薪在当前选取的统计周期内计入一次</small></div>
          <label><span>底薪</span><div className="input-affix"><b>¥</b><input type="number" min="0" step="0.01" required value={settingsForm.baseSalary} onChange={(event) => setSettingsForm({ ...settingsForm, baseSalary: event.target.value })} /></div></label>
          <label><span>员工个人社保缴纳</span><div className="input-affix"><b>¥</b><input type="number" min="0" step="0.01" required value={settingsForm.employeeSocialSecurityContribution} onChange={(event) => setSettingsForm({ ...settingsForm, employeeSocialSecurityContribution: event.target.value })} /></div></label>
          {bootstrap.workTypes.map((type) => (
            <label key={type}>
              <span><i style={{ background: bootstrap.workTypeColors[type] }} />{type}</span>
              <div className="input-affix suffix"><input type="number" min="0" max="1000" step="0.01" required value={settingsForm.commissionRates[type] ?? ""} onChange={(event) => setSettingsForm({ ...settingsForm, commissionRates: { ...settingsForm.commissionRates, [type]: event.target.value } })} /><b>%</b></div>
            </label>
          ))}
          <button className="primary-button payroll-rule-save" disabled={settingsSaving}><Save size={15} />{settingsSaving ? "保存中…" : "保存规则"}</button>
        </form>
      )}

      {calculation && (
        <>
          <section className="holiday-overtime-panel" aria-label="法定节假日加班">
            <header><div><h4>法定节假日加班</h4><p>日薪＝周期底薪 ÷ 本周期 {calculation.holidayOvertime.periodDays} 个自然日；当天底薪合计 3 倍，在周期底薪外补 2 倍日薪，提成照常计算。同一员工同一天只计一次。</p></div></header>
            <div className="holiday-overtime-overview">
              <span>基础日薪 <strong>¥{calculation.holidayOvertime.dailyBaseSalary.toFixed(4)}</strong></span>
              <span>已标记 <strong>{calculation.holidayOvertime.overtimeDays} 天</strong></span>
              <span>加班补发 <strong>{moneyText(calculation.holidayOvertime.extraSalary)}</strong></span>
            </div>
            <div className="holiday-overtime-days">
              {calculation.holidayOvertime.days.map((day) => <label className={`holiday-overtime-day ${day.enabled ? "selected" : ""}`} key={day.date}>
                <input className="no-print" type="checkbox" aria-label={`${day.date} ${day.name}加班`} checked={day.enabled} disabled={loading || savingHoliday != null} onChange={(event) => void markHoliday(day.date, event.target.checked)} />
                <span><strong>{day.date}</strong><small>{day.name}</small></span>
                <b>{savingHoliday === day.date ? "保存中…" : day.enabled ? "已标记加班" : "未标记"}</b>
              </label>)}
            </div>
            {!calculation.holidayOvertime.days.length && <p className="holiday-overtime-note">本周期没有已核验的法定节假日。</p>}
            {calculation.holidayOvertime.unverifiedYears.length > 0 && <p className="holiday-overtime-note">{calculation.holidayOvertime.unverifiedYears.join("、")} 年法定节假日日期尚未核验，暂不能新增该年份的加班标记。</p>}
            <p className="holiday-overtime-note">按员工和当前周期统计，不随店铺、项目或工作类型筛选改变；补发合计按未舍入日薪计算后保留两位小数。<span className="no-print"> 日期依据：{calculation.holidayOvertime.sources.map((source, index) => <span key={source.url}>{index > 0 ? " · " : ""}<a href={source.url} target="_blank" rel="noreferrer">{source.title}</a></span>)}</span></p>
          </section>
          <div className="payroll-summary">
            <article><span>周期底薪</span><strong>{moneyText(calculation.summary.baseSalary)}</strong><small>本周期计入一次</small></article>
            <article><span>法定节假日加班补发</span><strong>{moneyText(calculation.summary.holidayOvertimeExtra)}</strong><small>{calculation.holidayOvertime.overtimeDays} 天 × 日薪 × 2</small></article>
            <article><span>提成合计</span><strong>{moneyText(calculation.summary.commissionTotal)}</strong><small>{calculation.summary.includedRecords} 条有效记录</small></article>
            <article className="salary-deduction"><span>员工个人社保缴纳</span><strong>− {moneyText(calculation.summary.employeeSocialSecurityContribution)}</strong><small>从应计工资中扣除</small></article>
            <article className="salary-total"><span>应计工资总额</span><strong>{moneyText(calculation.summary.salaryTotal)}</strong><small>底薪 + 加班补发 + 提成 − 个人社保</small></article>
          </div>
          <div className="payroll-table-wrap">
            <table className="payroll-table">
              <thead><tr><th>日期 / 时间</th><th>工作内容</th><th>类型与默认比例</th><th>实收总金额</th><th>实际计算金额</th><th>实际计算比例</th><th>本项提成</th><th className="no-print">操作</th></tr></thead>
              <tbody>
                {calculation.rows.map((row) => {
                  const draft = drafts[row.id] || { amount: "", rate: "" };
                  const overridden = row.actualCalculationAmount != null || row.actualCalculationRate != null;
                  return (
                    <tr key={row.id} className={overridden ? "payroll-overridden" : ""}>
                      <td>{new Date(row.startAt).toLocaleDateString("zh-CN")}<small>{new Date(row.startAt).toLocaleTimeString("zh-CN", { hour: "2-digit", minute: "2-digit" })}–{new Date(row.endAt).toLocaleTimeString("zh-CN", { hour: "2-digit", minute: "2-digit" })}</small></td>
                      <td><strong>{row.title}</strong><small>{row.clientProject || row.storeName || "—"}</small></td>
                      <td><strong>{row.defaultCalculationRate.toFixed(2)}%</strong><small>{row.workTypes.map((type) => `${type} ${calculation.settings.commissionRates[type]?.toFixed(2) ?? "0.00"}%`).join(" + ")}</small></td>
                      <td>{moneyText(row.receivedAmount)}{!row.projectAmountCounted && <small>同项目金额已在上一条计入</small>}</td>
                      <td><input aria-label={`${row.title}实际计算金额`} type="number" min="0" step="0.01" value={draft.amount} placeholder={row.defaultCalculationAmount.toFixed(2)} onChange={(event) => setDrafts({ ...drafts, [row.id]: { ...draft, amount: event.target.value } })} /><small>有效：{moneyText(row.effectiveCalculationAmount)}</small></td>
                      <td><div className="input-affix suffix"><input aria-label={`${row.title}实际计算比例`} type="number" min="0" max="1000" step="0.01" value={draft.rate} placeholder={row.defaultCalculationRate.toFixed(2)} onChange={(event) => setDrafts({ ...drafts, [row.id]: { ...draft, rate: event.target.value } })} /><b>%</b></div><small>有效：{row.effectiveCalculationRate.toFixed(2)}%</small></td>
                      <td className="commission-cell"><strong>{moneyText(row.commission)}</strong></td>
                      <td className="payroll-row-actions no-print"><button className="text-button" disabled={savingRow === row.id} onClick={() => void saveOverride(row.id)}><Save size={13} />保存</button><button className="text-button muted" disabled={savingRow === row.id || !overridden} onClick={() => void saveOverride(row.id, true)}><RotateCcw size={13} />默认</button></td>
                    </tr>
                  );
                })}
                {!loading && calculation.rows.length === 0 && <tr><td colSpan={8} className="empty-table">当前范围没有可计薪的工作记录</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}
      {loading && !calculation && <div className="empty-table">正在计算工资…</div>}
    </section>
  );
}

function ProjectScheduleExportDialog({ project, query = "", rangeLabel = "全部日期，自动汇总该项目的所有日程", onClose }: { project: string; query?: string; rangeLabel?: string; onClose: () => void }) {
  const [columns, setColumns] = useState<ScheduleExportColumnKey[]>(defaultScheduleExportColumns);
  const exportQuery = useMemo(() => {
    const params = new URLSearchParams(query);
    params.set("project", project);
    params.set("columns", columns.join(","));
    return params.toString();
  }, [query, project, columns]);
  const toggleColumn = (key: ScheduleExportColumnKey) => setColumns((current) => current.includes(key) ? current.filter((item) => item !== key) : [...current, key]);

  return (
    <div className="schedule-export-backdrop no-print" onMouseDown={onClose}>
      <section className="schedule-export-dialog" role="dialog" aria-modal="true" aria-label="导出项目时间表" onMouseDown={(event) => event.stopPropagation()}>
        <header>
          <div><p className="eyebrow">PROJECT SCHEDULE</p><h3>导出“{project}”时间表</h3><small>{rangeLabel}</small></div>
          <button className="icon-button" onClick={onClose} aria-label="关闭导出设置"><X size={18} /></button>
        </header>
        <div className="schedule-export-controls">
          <strong>勾选要包含的内容</strong>
          <div><button type="button" onClick={() => setColumns(scheduleExportColumnOptions.map((item) => item.key))}>全选</button><button type="button" onClick={() => setColumns(defaultScheduleExportColumns)}>恢复常用项</button><button type="button" onClick={() => setColumns([])}>清空</button></div>
        </div>
        <div className="schedule-column-grid">
          {scheduleExportColumnOptions.map((option) => (
            <label className={columns.includes(option.key) ? "selected" : ""} key={option.key}>
              <input type="checkbox" checked={columns.includes(option.key)} onChange={() => toggleColumn(option.key)} />
              <span>{option.label}</span>
            </label>
          ))}
        </div>
        <footer>
          <small>已选择 {columns.length} 项</small>
          <div><button className="secondary-button" onClick={onClose}>取消</button>{columns.length > 0 ? <a className="primary-button" href={`/api/projects/schedule/export?${exportQuery}`} onClick={onClose}><FileSpreadsheet size={16} />导出 Excel</a> : <button className="primary-button" disabled><FileSpreadsheet size={16} />请先勾选内容</button>}</div>
        </footer>
      </section>
    </div>
  );
}

function ReportModal({ bootstrap, onClose }: { bootstrap: Bootstrap; onClose: () => void }) {
  const today = new Date();
  const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
  const [from, setFrom] = useState(dateInput(monthStart));
  const [to, setTo] = useState(dateInput(today));
  const [storeName, setStoreName] = useState("");
  const [employeeId, setEmployeeId] = useState("");
  const [workType, setWorkType] = useState("");
  const [project, setProject] = useState("");
  const [rows, setRows] = useState<ReportRow[]>([]);
  const [summary, setSummary] = useState<ReportSummary>({ count: 0, totalMinutes: 0, estimatedTotal: 0, totalAmount: 0, byType: {}, byProject: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [scheduleExportOpen, setScheduleExportOpen] = useState(false);

  const query = useMemo(() => {
    const start = new Date(`${from}T00:00:00`);
    const end = new Date(`${to}T23:59:59.999`);
    const params = new URLSearchParams({ from: start.toISOString(), to: end.toISOString() });
    if (storeName) params.set("storeName", storeName);
    if (employeeId) params.set("employeeId", employeeId);
    if (workType) params.set("workType", workType);
    if (project) params.set("project", project);
    return params;
  }, [from, to, storeName, employeeId, workType, project]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await request<{ rows: ReportRow[]; summary: ReportSummary }>(`/api/reports?${query}`);
      setRows(data.rows);
      setSummary(data.summary);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "生成记录失败");
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => { void load(); }, [load]);

  const openScheduleExport = (projectName = project) => {
    if (!projectName) return;
    setProject(projectName);
    setScheduleExportOpen(true);
  };

  return (
    <div className="modal-backdrop report-backdrop">
      <section className="modal-card report-modal" role="dialog" aria-modal="true" aria-label="工作记录表">
        <header className="modal-header report-heading">
          <div>
            <p className="eyebrow">WORK LOG</p>
            <h2>工作记录表</h2>
            <p>{from} 至 {to}</p>
          </div>
          <button className="icon-button no-print" onClick={onClose} aria-label="关闭"><X size={19} /></button>
        </header>
        <div className="report-filters no-print">
          <label><span>开始日期</span><input type="date" value={from} onChange={(event) => setFrom(event.target.value)} /></label>
          <label><span>结束日期</span><input type="date" value={to} min={from} onChange={(event) => setTo(event.target.value)} /></label>
          <label><span>店铺</span><select value={storeName} onChange={(event) => setStoreName(event.target.value)}><option value="">全部店铺</option>{bootstrap.storeNames.map((name) => <option value={name} key={name}>{name}</option>)}</select></label>
          <label><span>负责人</span><select value={employeeId} onChange={(event) => setEmployeeId(event.target.value)}><option value="">全部负责人</option>{bootstrap.users.filter((item) => item.active).map((item) => <option value={item.id} key={item.id}>{item.displayName}</option>)}</select></label>
          <label><span>工作类型</span><select value={workType} onChange={(event) => setWorkType(event.target.value)}><option value="">全部类型</option>{bootstrap.workTypes.map((item) => <option key={item}>{item}</option>)}</select></label>
          <label><span>项目</span><select value={project} onChange={(event) => { setProject(event.target.value); if (!event.target.value) setScheduleExportOpen(false); }}><option value="">全部项目</option>{(bootstrap.projectNames || []).map((name) => <option value={name} key={name}>{name}</option>)}</select></label>
          <button className="secondary-button refresh-report" onClick={() => void load()}><RefreshCw size={16} />刷新</button>
        </div>
        {error && <div className="form-error">{error}</div>}
        <div className="report-body-scroll">
          <div className="report-summary">
            <article><span>记录数量</span><strong>{summary.count}</strong><small>条日程</small></article>
            <article><span>总工时</span><strong>{(summary.totalMinutes / 60).toFixed(1)}</strong><small>小时</small></article>
            <article><span>项目预估总价</span><strong>{moneyText(summary.estimatedTotal)}</strong><small>按项目计价方式自动计算</small></article>
            <article><span>项目实收总金额</span><strong>{moneyText(summary.totalAmount)}</strong><small>同一项目只计一次</small></article>
            {bootstrap.workTypes.filter((type) => summary.byType[type]).slice(0, 4).map((type) => (
              <article key={type}><span><i style={{ background: bootstrap.workTypeColors[type] }} />{type}</span><strong>{((summary.byType[type] || 0) / 60).toFixed(1)}</strong><small>小时</small></article>
            ))}
          </div>
          <section className="project-summary-panel" aria-label="按项目汇总">
            <header>
              <div><p className="eyebrow">PROJECT TOTALS</p><h3>按项目汇总</h3></div>
              <small>同一项目关联的多条日程会自动合并统计</small>
            </header>
            <div className="project-summary-table-wrap">
              <table className="project-summary-table">
                <thead><tr><th>项目</th><th>日程数量</th><th>总时长</th><th>计价方式</th><th>项目单价</th><th>项目预估总价</th><th>项目实收总金额</th><th className="no-print">时间表</th></tr></thead>
                <tbody>
                  {summary.byProject.map((item) => (
                    <tr key={item.project}><td><strong>{item.project}</strong></td><td>{item.count} 条</td><td>{formatHours(item.totalMinutes)}</td><td>{item.pricingMode === "quantity" ? "按数量" : "按时间"}</td><td>{moneyText(item.unitPrice)}{item.unitPrice == null ? "" : item.pricingMode === "quantity" ? "/条" : "/小时"}</td><td>{moneyText(item.estimatedTotal)}</td><td className="project-amount">{moneyText(item.totalAmount)}</td><td className="no-print"><button className="project-schedule-button" onClick={() => openScheduleExport(item.project)}><Download size={13} />导出</button></td></tr>
                  ))}
                  {!loading && summary.byProject.length === 0 && <tr><td colSpan={8} className="empty-table">当前范围还没有可汇总的项目日程</td></tr>}
                </tbody>
              </table>
            </div>
          </section>
          <div className="report-table-wrap">
            <table className="report-table">
              <thead><tr><th>日期</th><th>时间</th><th>类型</th><th>项目</th><th>工作内容</th><th>店铺</th><th>负责人</th><th>工时</th><th>计价方式</th><th>项目单价</th><th>项目预估总价</th><th>项目实收总金额</th><th>备注</th></tr></thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td>{new Date(row.startAt).toLocaleDateString("zh-CN")}</td>
                    <td>{new Date(row.startAt).toLocaleTimeString("zh-CN", { hour: "2-digit", minute: "2-digit" })}–{new Date(row.endAt).toLocaleTimeString("zh-CN", { hour: "2-digit", minute: "2-digit" })}</td>
                    <td><span className="type-chip">{eventWorkTypes(row).map((type) => <i style={{ background: bootstrap.workTypeColors[type] }} key={type} />)}{eventWorkTypes(row).join("、")}</span></td>
                    <td>{row.clientProject || "—"}</td><td><strong>{row.title}</strong></td>
                    <td>{row.storeName || "—"}</td><td>{row.employeeName}</td><td>{formatHours(row.durationMinutes)}</td>
                    <td>{row.clientProject ? (row.pricingMode === "quantity" ? "按数量" : "按时间") : "—"}</td><td>{row.unitPrice == null ? "—" : `${moneyText(row.unitPrice)}${row.pricingMode === "quantity" && row.clientProject ? "/条" : "/小时"}`}</td><td>{row.projectEstimatedTotal == null ? "—" : moneyText(row.projectEstimatedTotal)}</td><td>{row.totalPrice == null ? "—" : moneyText(row.totalPrice)}</td><td>{row.notes || "—"}</td>
                  </tr>
                ))}
                {!loading && rows.length === 0 && <tr><td colSpan={13} className="empty-table">选取范围内没有工作记录</td></tr>}
                {loading && <tr><td colSpan={13} className="empty-table">正在生成…</td></tr>}
              </tbody>
            </table>
          </div>
          {bootstrap.user.role === "admin" && <PayrollPanel key={`${employeeId}:${query.toString()}`} bootstrap={bootstrap} query={query.toString()} employeeId={employeeId} />}
        </div>
        {scheduleExportOpen && project && <ProjectScheduleExportDialog project={project} query={query.toString()} rangeLabel={`${from} 至 ${to}，仅导出当前筛选范围内的日程`} onClose={() => setScheduleExportOpen(false)} />}
        <footer className="report-actions no-print">
          <button className="secondary-button" onClick={() => window.print()}><Printer size={16} />打印 / 存为 PDF</button>
          <a className="secondary-button" href={`/api/reports/export?${query}&format=csv`}><Download size={16} />CSV</a>
          <button className="secondary-button" disabled={!project} title={project ? `导出 ${project} 的时间表` : "请先在上方选择一个项目"} onClick={() => openScheduleExport()}><Download size={16} />{project ? "项目时间表" : "先选项目再导出"}</button>
          <a className="primary-button" href={`/api/reports/export?${query}&format=xlsx`}><FileSpreadsheet size={16} />导出 Excel</a>
        </footer>
      </section>
    </div>
  );
}

const WEEKDAY_NAMES = ["周日", "周一", "周二", "周三", "周四", "周五", "周六"];
const STORE_DISABLED_MODES: Array<{ value: StoreDisabledTime["mode"]; label: string }> = [
  { value: "daily", label: "每天固定时段" },
  { value: "weeklyDay", label: "每周某天全天" },
  { value: "weeklyTime", label: "每周某天的时段" },
  { value: "date", label: "指定日期全天" },
  { value: "once", label: "一次性起止时间" }
];

function newStoreDisabledDraft(storeName: string): StoreDisabledTime {
  const today = dateInput();
  return { id: "", storeName, mode: "daily", weekday: 1, date: today, startTime: "09:00", endTime: "18:00", startAt: `${today}T09:00`, endAt: `${today}T18:00` };
}

function storeDisabledDescription(rule: StoreDisabledTime) {
  if (rule.mode === "daily") return `每天 ${rule.startTime}–${rule.endTime}${rule.startTime! > rule.endTime! ? "（次日）" : ""}`;
  if (rule.mode === "weeklyDay") return `每${WEEKDAY_NAMES[rule.weekday!]} 全天`;
  if (rule.mode === "weeklyTime") return `每${WEEKDAY_NAMES[rule.weekday!]} ${rule.startTime}–${rule.endTime}${rule.startTime! > rule.endTime! ? "（次日）" : ""}`;
  if (rule.mode === "date") return `${rule.date} 全天`;
  return `${rule.startAt?.replace("T", " ")} 至 ${rule.endAt?.replace("T", " ")}`;
}

function TeamModal({ bootstrap, onClose, refresh, picking, pickedRange, onPick }: { bootstrap: Bootstrap; onClose: () => void; refresh: () => Promise<void>; picking: boolean; pickedRange: StoreDisabledTime | null; onPick: (storeName: string) => void }) {
  const [form, setForm] = useState({ username: "", displayName: "", password: "", role: "staff" as Role, branchId: bootstrap.branches.find((item) => item.active)?.id || 1 });
  const [typeForm, setTypeForm] = useState(() => bootstrap.workTypes.map((name) => ({ originalName: name, name, color: bootstrap.workTypeColors[name] })));
  const [newType, setNewType] = useState({ name: "", color: "#00aec4" });
  const [savingType, setSavingType] = useState("");
  const [typesSaved, setTypesSaved] = useState(false);
  const [storeColors, setStoreColors] = useState(() => ({ ...bootstrap.storeColors }));
  const [disabledTimes, setDisabledTimes] = useState<StoreDisabledTime[]>(() => bootstrap.storeDisabledTimes);
  const [disabledDraft, setDisabledDraft] = useState<StoreDisabledTime>(() => newStoreDisabledDraft(bootstrap.storeNames[0]));
  const [savingStores, setSavingStores] = useState(false);
  const [storesSaved, setStoresSaved] = useState(false);
  const [error, setError] = useState("");
  const storeSettingsVersion = JSON.stringify([bootstrap.storeColors, bootstrap.storeDisabledTimes]);

  useEffect(() => {
    setTypeForm(bootstrap.workTypes.map((name) => ({ originalName: name, name, color: bootstrap.workTypeColors[name] })));
  }, [bootstrap.workTypes, bootstrap.workTypeColors]);

  useEffect(() => {
    setStoreColors({ ...bootstrap.storeColors });
    setDisabledTimes(bootstrap.storeDisabledTimes);
  }, [storeSettingsVersion]);

  useEffect(() => {
    if (!pickedRange) return;
    setDisabledDraft(pickedRange);
    setError("");
    setStoresSaved(false);
  }, [pickedRange]);

  function addDisabledTime(event: React.FormEvent) {
    event.preventDefault();
    const rule = { ...disabledDraft, id: disabledDraft.id || crypto.randomUUID() };
    if (rule.mode === "once" && (!rule.startAt || !rule.endAt || rule.startAt >= rule.endAt)) return setError("一次性禁用时间的结束时间须晚于开始时间");
    if ((rule.mode === "daily" || rule.mode === "weeklyTime") && (!rule.startTime || !rule.endTime || rule.startTime === rule.endTime)) return setError("禁用时段的起止时间不能相同");
    setError("");
    setDisabledTimes((current) => [...current.filter((item) => item.id !== rule.id), rule]);
    setDisabledDraft(newStoreDisabledDraft(rule.storeName));
    setStoresSaved(false);
  }

  async function saveStores() {
    setSavingStores(true);
    setStoresSaved(false);
    setError("");
    try {
      await request("/api/settings/stores", {
        method: "PATCH",
        body: JSON.stringify({ colors: storeColors, disabledTimes })
      }, bootstrap.csrfToken);
      await refresh();
      setStoresSaved(true);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "棚位设置保存失败");
    } finally {
      setSavingStores(false);
    }
  }

  async function createUser(event: React.FormEvent) {
    event.preventDefault();
    setError("");
    try {
      await request("/api/users", { method: "POST", body: JSON.stringify(form) }, bootstrap.csrfToken);
      setForm((current) => ({ ...current, username: "", displayName: "", password: "" }));
      await refresh();
    } catch (reason) { setError(reason instanceof Error ? reason.message : "创建失败"); }
  }

  async function updateUser(user: User, changes: Partial<User> & { password?: string }) {
    setError("");
    try {
      await request(`/api/users/${user.id}`, { method: "PATCH", body: JSON.stringify(changes) }, bootstrap.csrfToken);
      await refresh();
    } catch (reason) { setError(reason instanceof Error ? reason.message : "修改失败"); }
  }

  async function resetPassword(user: User) {
    const password = window.prompt(`为“${user.displayName}”设置新密码（至少 8 位）`);
    if (!password) return;
    await updateUser(user, { password });
  }

  async function renameUser(user: User) {
    const displayName = window.prompt(`修改“${user.displayName}”的显示名称`, user.displayName)?.trim();
    if (!displayName || displayName === user.displayName) return;
    await updateUser(user, { displayName });
  }

  async function saveWorkType(item: { originalName: string; name: string; color: string }) {
    setError("");
    setTypesSaved(false);
    setSavingType(item.originalName);
    try {
      await request(`/api/work-types/${encodeURIComponent(item.originalName)}`, {
        method: "PATCH",
        body: JSON.stringify({ name: item.name, color: item.color })
      }, bootstrap.csrfToken);
      await refresh();
      setTypesSaved(true);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "工作类型保存失败");
    } finally {
      setSavingType("");
    }
  }

  async function addWorkType(event: React.FormEvent) {
    event.preventDefault();
    setError("");
    setSavingType("__new__");
    try {
      await request("/api/work-types", { method: "POST", body: JSON.stringify(newType) }, bootstrap.csrfToken);
      setNewType({ name: "", color: "#00aec4" });
      await refresh();
      setTypesSaved(true);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "工作类型添加失败");
    } finally {
      setSavingType("");
    }
  }

  async function deleteWorkType(name: string) {
    if (!window.confirm(`确定删除工作类型“${name}”吗？`)) return;
    setError("");
    setSavingType(name);
    try {
      await request(`/api/work-types/${encodeURIComponent(name)}`, { method: "DELETE" }, bootstrap.csrfToken);
      await refresh();
      setTypesSaved(true);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "工作类型删除失败");
    } finally {
      setSavingType("");
    }
  }

  return (
    <div className="modal-backdrop" style={picking ? { display: "none" } : undefined}>
      <section className="modal-card team-modal" role="dialog" aria-modal="true" aria-label="设置">
        <header className="modal-header">
          <div><p className="eyebrow">SYSTEM SETTINGS</p><h2>设置</h2></div>
          <button className="icon-button" aria-label="关闭设置" onClick={onClose}><X size={19} /></button>
        </header>
        {error && <div className="form-error">{error}</div>}
        <div className="team-content">
            <section className="store-settings">
              <header><div><p className="eyebrow">STUDIOS</p><h3>棚位颜色与禁用时间</h3><small>灰色仅作时间提示，仍可在这些时段拉取、拖动或编辑日程。空白日期可先在左侧选择棚位查看。</small></div></header>
              <div className="store-color-grid">
                {bootstrap.storeNames.map((name) => <label key={name} className="store-color-row">
                  <input type="color" aria-label={`${name}代表色`} value={storeColors[name]} onChange={(event) => { setStoreColors((current) => ({ ...current, [name]: event.target.value })); setStoresSaved(false); }} />
                  <span>{name}</span>
                </label>)}
              </div>
              <div className="store-disabled-settings">
                <strong>禁用时间</strong>
                <button type="button" className="secondary-button" onClick={() => onPick(disabledDraft.storeName)}>从日历拉取</button>
                <small>在棚位列上拖选时段，自动填入一次性规则；添加规则后保存棚位设置。</small>
                <div className="store-disabled-list">
                  {disabledTimes.length === 0 && <small>暂未设置</small>}
                  {disabledTimes.map((rule) => <div className="store-disabled-row" key={rule.id}>
                    <i className="filter-dot" style={{ background: storeColors[rule.storeName] }} />
                    <span><b>{rule.storeName}</b> · {storeDisabledDescription(rule)}</span>
                    <button type="button" className="text-button" onClick={() => { setDisabledDraft(rule); setStoresSaved(false); }}>修改</button>
                    <button type="button" className="icon-button" aria-label={`删除${rule.storeName}${storeDisabledDescription(rule)}`} onClick={() => { setDisabledTimes((current) => current.filter((item) => item.id !== rule.id)); setStoresSaved(false); }}><Trash2 size={14} /></button>
                  </div>)}
                </div>
                <form className="store-disabled-form" onSubmit={addDisabledTime}>
                  <select aria-label="棚位" value={disabledDraft.storeName} onChange={(event) => setDisabledDraft({ ...disabledDraft, storeName: event.target.value })}>{bootstrap.storeNames.map((name) => <option key={name}>{name}</option>)}</select>
                  <select aria-label="重复方式" value={disabledDraft.mode} onChange={(event) => setDisabledDraft({ ...disabledDraft, mode: event.target.value as StoreDisabledTime["mode"] })}>{STORE_DISABLED_MODES.map((mode) => <option key={mode.value} value={mode.value}>{mode.label}</option>)}</select>
                  {(disabledDraft.mode === "weeklyDay" || disabledDraft.mode === "weeklyTime") && <select aria-label="星期" value={disabledDraft.weekday} onChange={(event) => setDisabledDraft({ ...disabledDraft, weekday: Number(event.target.value) })}>{WEEKDAY_NAMES.map((name, index) => <option key={name} value={index}>{name}</option>)}</select>}
                  {(disabledDraft.mode === "daily" || disabledDraft.mode === "weeklyTime") && <><input aria-label="开始时间" type="time" required value={disabledDraft.startTime || ""} onChange={(event) => setDisabledDraft({ ...disabledDraft, startTime: event.target.value })} /><span>至</span><input aria-label="结束时间" type="time" required value={disabledDraft.endTime || ""} onChange={(event) => setDisabledDraft({ ...disabledDraft, endTime: event.target.value })} /></>}
                  {disabledDraft.mode === "date" && <input aria-label="指定日期" type="date" required value={disabledDraft.date || ""} onChange={(event) => setDisabledDraft({ ...disabledDraft, date: event.target.value })} />}
                  {disabledDraft.mode === "once" && <><input aria-label="开始日期时间" type="datetime-local" required value={disabledDraft.startAt || ""} onChange={(event) => setDisabledDraft({ ...disabledDraft, startAt: event.target.value })} /><span>至</span><input aria-label="结束日期时间" type="datetime-local" required value={disabledDraft.endAt || ""} onChange={(event) => setDisabledDraft({ ...disabledDraft, endAt: event.target.value })} /></>}
                  <button className="secondary-button" type="submit"><Plus size={14} />{disabledDraft.id ? "更新规则" : "添加规则"}</button>
                  {disabledDraft.id && <button className="text-button" type="button" onClick={() => setDisabledDraft(newStoreDisabledDraft(disabledDraft.storeName))}>取消修改</button>}
                </form>
              </div>
              <div className="store-settings-footer"><button className="primary-button" type="button" disabled={savingStores} onClick={() => void saveStores()}><Save size={14} />{savingStores ? "保存中…" : "保存棚位设置"}</button>{storesSaved && <span className="color-save-success"><Check size={14} />已同步到日历</span>}</div>
            </section>
            <section className="work-type-color-settings">
              <header>
                <div><p className="eyebrow">WORK TYPES</p><h3>工作类型</h3><small>管理员可修改名称和颜色，也可以新增或删除未被日程使用的类型。</small></div>
              </header>
              <div className="work-type-color-grid">
                {typeForm.map((item) => (
                  <div className="work-type-color-row" key={item.originalName}>
                    <input
                      type="color"
                      aria-label={`${item.originalName}颜色`}
                      value={item.color}
                      onChange={(event) => { setTypeForm((current) => current.map((row) => row.originalName === item.originalName ? { ...row, color: event.target.value } : row)); setTypesSaved(false); }}
                    />
                    <input
                      aria-label={`${item.originalName}名称`}
                      maxLength={20}
                      value={item.name}
                      onChange={(event) => { setTypeForm((current) => current.map((row) => row.originalName === item.originalName ? { ...row, name: event.target.value } : row)); setTypesSaved(false); }}
                    />
                    <button className="text-button type-save-button" type="button" disabled={Boolean(savingType) || (!item.name.trim())} onClick={() => void saveWorkType({ ...item, name: item.name.trim() })}><Save size={13} />{savingType === item.originalName ? "保存中" : "保存"}</button>
                    <button className="icon-button type-delete-button" type="button" aria-label={`删除${item.originalName}`} disabled={Boolean(savingType) || typeForm.length <= 1} onClick={() => void deleteWorkType(item.originalName)}><Trash2 size={14} /></button>
                  </div>
                ))}
              </div>
              <form className="create-work-type-form" onSubmit={addWorkType}>
                <input type="color" aria-label="新工作类型颜色" value={newType.color} onChange={(event) => setNewType({ ...newType, color: event.target.value })} />
                <input required maxLength={20} placeholder="新工作类型名称" value={newType.name} onChange={(event) => setNewType({ ...newType, name: event.target.value })} />
                <button className="secondary-button" disabled={Boolean(savingType)}><Plus size={15} />{savingType === "__new__" ? "添加中…" : "添加类型"}</button>
              </form>
              {typesSaved && <div className="color-save-success"><Check size={14} />工作类型已同步</div>}
            </section>
            <form className="create-user-form" onSubmit={createUser}>
              <h3>添加账号</h3>
              <input required placeholder="姓名" value={form.displayName} onChange={(event) => setForm({ ...form, displayName: event.target.value })} />
              <input required placeholder="用户名" value={form.username} onChange={(event) => setForm({ ...form, username: event.target.value })} />
              <input required type="password" minLength={8} placeholder="初始密码（至少 8 位）" value={form.password} onChange={(event) => setForm({ ...form, password: event.target.value })} />
              <select value={form.role} onChange={(event) => setForm({ ...form, role: event.target.value as Role })}>{bootstrap.roles.map((role) => <option value={role} key={role}>{roleLabel(role)}</option>)}</select>
              <button className="primary-button"><Plus size={16} />添加账号</button>
            </form>
            <div className="user-list">
              {bootstrap.users.map((user) => (
                <article className={`user-row ${!user.active ? "inactive" : ""}`} key={user.id}>
                  <span className="avatar">{user.displayName.slice(0, 1)}</span>
                  <div className="user-main"><strong>{user.displayName}</strong><small>@{user.username}</small></div>
                  <select
                    className="user-role-select"
                    aria-label={`${user.displayName}账号类型`}
                    value={user.role}
                    disabled={user.id === bootstrap.user.id}
                    onChange={(event) => void updateUser(user, { role: event.target.value as Role })}
                  >
                    {bootstrap.roles.map((role) => <option value={role} key={role}>{roleLabel(role)}</option>)}
                  </select>
                  <div className="user-actions">
                    <button className="text-button" onClick={() => void renameUser(user)}>修改名称</button>
                    <button className="text-button" onClick={() => void resetPassword(user)}>重置密码</button>
                    <button className="text-button" disabled={user.id === bootstrap.user.id} onClick={() => void updateUser(user, { active: !user.active })}>{user.active ? "停用" : "启用"}</button>
                  </div>
                </article>
              ))}
            </div>
          </div>
      </section>
    </div>
  );
}

function DayLocationMenu({ date, title, prefs, storeColors, frame, onChange, onClose }: {
  date: string;
  title: string;
  prefs: DayLocationPrefs;
  storeColors: Record<string, string>;
  frame: HTMLElement | null;
  onChange: (date: string, stores: StoreSubcolumn[] | null) => void;
  onClose: () => void;
}) {
  const [position, setPosition] = useState<{ left: number; top: number } | null>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const chosen = prefs[date];
  const expanded = Boolean(chosen?.length);
  const scheduledColumn = frame?.querySelector<HTMLElement>(`.fc-timegrid-col[data-date="${date}"]`);
  const scheduled = scheduledColumn ? scheduledStoresForDay(scheduledColumn) : [];
  const shown = expanded ? STORE_SUBCOLUMNS.filter((name) => (chosen ?? []).includes(name) || scheduled.includes(name)) : [];

  const place = useCallback(() => {
    const anchor = document.querySelector<HTMLElement>(`.fc-col-header-cell[data-date="${date}"] .day-location-toggle`);
    const rect = anchor?.getBoundingClientRect();
    if (!rect || !rect.width) {
      onCloseRef.current();
      return;
    }
    const width = 236;
    setPosition({ left: Math.max(8, Math.min(rect.right - width, window.innerWidth - width - 8)), top: rect.bottom + 6 });
  }, [date]);

  useEffect(() => {
    place();
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    const close = (event: Event) => {
      const target = event.target as Element | null;
      if (target?.closest?.(".day-location-menu, .day-location-toggle")) return;
      onCloseRef.current();
    };
    const escape = (event: KeyboardEvent) => { if (event.key === "Escape") onCloseRef.current(); };
    document.addEventListener("pointerdown", close, true);
    document.addEventListener("keydown", escape);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
      document.removeEventListener("pointerdown", close, true);
      document.removeEventListener("keydown", escape);
    };
  }, [place]);

  // Expanding changes the day's width, which moves its header button.
  useEffect(() => {
    const frameId = window.requestAnimationFrame(() => window.requestAnimationFrame(place));
    return () => window.cancelAnimationFrame(frameId);
  }, [chosen, place]);

  const toggleStore = (name: StoreSubcolumn) => {
    const next = shown.includes(name) ? shown.filter((item) => item !== name) : STORE_SUBCOLUMNS.filter((item) => shown.includes(item) || item === name);
    onChange(date, next);
  };

  return (
    <div className="day-location-menu" role="dialog" aria-label={`${title} 地点列`} style={{ left: position?.left ?? 0, top: position?.top ?? 0, visibility: position ? "visible" : "hidden" }}>
      <header><strong>{title}</strong><small>地点列</small></header>
      <label className="day-location-switch">
        <input type="checkbox" checked={expanded} onChange={() => onChange(date, expanded ? null : [...STORE_SUBCOLUMNS])} />
        <span>展开地点列</span>
      </label>
      {expanded
        ? <div className="day-location-stores">
            {STORE_SUBCOLUMNS.map((name) => {
              const locked = scheduled.includes(name);
              return (
                <label key={name} className={locked ? "locked" : ""} title={locked ? "当天该地点已有日程，需保持显示" : undefined}>
                  <input type="checkbox" checked={shown.includes(name)} disabled={locked} onChange={() => toggleStore(name)} />
                  <i className="filter-dot" style={{ background: storeColors[name] || "#a8afb9" }} />
                  <span>{name}</span>
                  {locked && <small>有日程</small>}
                </label>
              );
            })}
            <button type="button" className="text-button" onClick={() => onChange(date, null)}>恢复自动</button>
          </div>
        : <p>自动：同一时间在多个地点都有日程时才分列，其余合并为一列。</p>}
    </div>
  );
}

function App() {
  const [bootstrap, setBootstrap] = useState<Bootstrap | null>(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [calendarTitle, setCalendarTitle] = useState("");
  const [selectedDate, setSelectedDate] = useState(dateInput());
  const [view, setView] = useState(() => window.innerWidth <= 600 ? "timeGridDay" : "rollingTimeGrid");
  const [storeFilter, setStoreFilter] = useState("");
  const [dayLocationPrefs, setDayLocationPrefs] = useState<DayLocationPrefs>(loadDayLocationPrefs);
  const [dayLocationMenu, setDayLocationMenu] = useState<{ date: string; title: string } | null>(null);
  const [employeeFilter, setEmployeeFilter] = useState("");
  const [typeFilters, setTypeFilters] = useState<Set<string>>(new Set());
  const [eventModal, setEventModal] = useState<EventRecord | null>(null);
  const [inlineDraft, setInlineDraft] = useState<EventRecord | null>(null);
  const [inlineEditorStyle, setInlineEditorStyle] = useState<React.CSSProperties>({ visibility: "hidden" });
  const [dayWidth, setDayWidth] = useState(() => {
    const saved = Number(window.localStorage.getItem("ale-calendar-day-width-v3"));
    return Number.isFinite(saved) && saved >= 56 && saved <= 220 ? saved : 172;
  });
  const [overlapExpansionPercent, setOverlapExpansionPercent] = useState(() => {
    const saved = Number(window.localStorage.getItem("ale-calendar-overlap-expansion-v1"));
    return Number.isFinite(saved) && saved >= 25 && saved <= 150 ? saved : 100;
  });
  const [lateHoursExpanded, setLateHoursExpanded] = useState(() => window.localStorage.getItem("ale-calendar-late-hours-v1") === "true");
  useEffect(() => {
    window.localStorage.setItem("ale-calendar-late-hours-v1", String(lateHoursExpanded));
    const frame = window.requestAnimationFrame(() => {
      calendarRef.current?.getApi().updateSize();
      scheduleCrossOverlapLayout();
    });
    return () => window.cancelAnimationFrame(frame);
  }, [lateHoursExpanded]);
  const [hourAxis, setHourAxis] = useState<{ top: number; height: number; markers: Array<{ label: string; top: number }> } | null>(null);
  const [rangeVersion, setRangeVersion] = useState(0);
  const [reportOpen, setReportOpen] = useState(false);
  const [scheduleExportProject, setScheduleExportProject] = useState("");
  const [teamOpen, setTeamOpen] = useState(false);
  const [pickingDisabledTime, setPickingDisabledTime] = useState(false);
  const [disabledPickStore, setDisabledPickStore] = useState("");
  const [pickedDisabledRange, setPickedDisabledRange] = useState<StoreDisabledTime | null>(null);
  const disabledPickStartStoreRef = useRef<string | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [reloadVersion, setReloadVersion] = useState(0);
  const [search, setSearch] = useState("");
  const [toast, setToast] = useState("");
  const [notificationFeed, setNotificationFeed] = useState<NotificationFeed>({ retentionDays: 7, recentChanges: [], pendingAssignments: [], reviewTasks: [] });
  const [acknowledgingEventId, setAcknowledgingEventId] = useState<number | null>(null);
  const [eventHoverPreview, setEventHoverPreview] = useState<EventHoverPreviewState | null>(null);
  const calendarRef = useRef<FullCalendar | null>(null);
  const bootstrapRef = useRef<Bootstrap | null>(bootstrap);
  const dayLocationPrefsRef = useRef(dayLocationPrefs);
  const calendarFrameRef = useRef<HTMLElement | null>(null);
  const rollingRangeStartRef = useRef(new Date());
  const focusDateRef = useRef(new Date());
  const dayWidthRef = useRef(dayWidth);
  const overlapExpansionPercentRef = useRef(overlapExpansionPercent);
  const recenterTimeoutRef = useRef<number | null>(null);
  const copyHandleCleanupRef = useRef<(() => void) | null>(null);
  const crossOverlapFrameRef = useRef<number | null>(null);
  const calendarInteractionActiveRef = useRef(false);
  const ownEventBroadcastRef = useRef({ count: 0, until: 0 });
  const eventMutationGenerationRef = useRef(0);
  const confirmedEventsRef = useRef(new Map<number, ConfirmedMutation<EventRecord>>());
  const pendingMovesRef = useRef(new Map<number, EventRecord>());
  const [saveError, setSaveError] = useState("");
  const undoStackRef = useRef<CalendarUndoAction[]>([]);
  const undoBusyRef = useRef(false);
  const undoShortcutHeldRef = useRef(false);
  const lastUndoShortcutAtRef = useRef(0);
  const undoLatestActionRef = useRef<() => void>(() => {});
  bootstrapRef.current = bootstrap;
  dayLocationPrefsRef.current = dayLocationPrefs;

  function scheduleCrossOverlapLayout() {
    // FullCalendar can mount/unmount its drag mirror repeatedly while the
    // pointer crosses cells. Measuring every card and recalculating all 56 day
    // widths from those callbacks forces synchronous layout on every pointer
    // frame. Restore the precise layout once interaction ends instead.
    if (calendarInteractionActiveRef.current) return;
    if (crossOverlapFrameRef.current != null) return;
    crossOverlapFrameRef.current = window.requestAnimationFrame(() => {
      crossOverlapFrameRef.current = null;
      const frame = calendarFrameRef.current;
      const isRolling = Boolean(frame?.classList.contains("rolling-calendar"));
      const centerDate = isRolling ? dateAtScrollerCenter() : null;
      if (isRolling) applyRollingDayColumnWidths(
        frame,
        dayWidthRef.current,
        overlapExpansionPercentRef.current,
        dayLocationPrefsRef.current
      );
      else clearRollingDayColumnWidths(frame);
      if (frame?.classList.contains("location-columns")) applyLocationSubcolumns(frame, bootstrapRef.current, storeFilter, dayLocationPrefsRef.current);
      else applyStaggeredOverlapLayout(frame);
      fitEventCardContent(frame);
      if (centerDate) scrollDateToCenter(centerDate);
    });
  }

  function beginCalendarInteraction() {
    calendarInteractionActiveRef.current = true;
    calendarFrameRef.current?.classList.add("calendar-interaction-active");
    clearCollaborationGroupHighlight();
    if (crossOverlapFrameRef.current != null) {
      window.cancelAnimationFrame(crossOverlapFrameRef.current);
      crossOverlapFrameRef.current = null;
    }
    // Clear the custom clipped overlap shape once so FullCalendar can move a
    // plain rectangle. Keep day widths frozen and do no further full-calendar
    // measurements until the pointer is released.
    if (!calendarFrameRef.current?.classList.contains("location-columns")) applyStaggeredOverlapLayout(calendarFrameRef.current, true);
  }

  function endCalendarInteraction() {
    calendarInteractionActiveRef.current = false;
    calendarFrameRef.current?.classList.remove("calendar-interaction-active");
    window.requestAnimationFrame(scheduleCrossOverlapLayout);
  }

  function clearCollaborationGroupHighlight() {
    const frame = calendarFrameRef.current;
    if (!frame) return;
    frame.classList.remove("collaboration-group-hover-active");
    for (const element of frame.querySelectorAll<HTMLElement>(".collaboration-group-highlight, .collaboration-group-highlight-harness")) {
      element.classList.remove("collaboration-group-highlight", "collaboration-group-highlight-harness");
    }
  }

  function highlightCollaborationGroup(value: EventRecord) {
    const frame = calendarFrameRef.current;
    const groupId = value.collaborationRootEventId ?? value.id;
    clearCollaborationGroupHighlight();
    if (!frame || groupId == null) return;

    const matches = Array.from(frame.querySelectorAll<HTMLElement>(".event-inner[data-collaboration-group-id]"))
      .filter((element) => element.dataset.collaborationGroupId === String(groupId));
    const eventElements = new Set(matches.map((element) => element.closest<HTMLElement>(".fc-event")).filter(Boolean) as HTMLElement[]);
    if (eventElements.size < 2) return;

    frame.classList.add("collaboration-group-hover-active");
    for (const element of eventElements) {
      element.classList.add("collaboration-group-highlight");
      element.closest<HTMLElement>(".fc-timegrid-event-harness")?.classList.add("collaboration-group-highlight-harness");
    }
  }

  function findHorizontalScroller() {
    const frame = calendarFrameRef.current;
    if (frame && frame.scrollWidth > frame.clientWidth + 4) return frame;
    const candidates = Array.from(calendarFrameRef.current?.querySelectorAll<HTMLElement>(".fc-scroller") || [])
      .filter((element) => element.scrollWidth > element.clientWidth + 4);
    return candidates.sort((first, second) => second.clientHeight - first.clientHeight)[0] || null;
  }

  function calendarDateAtClientX(clientX: number) {
    const columns = Array.from(calendarFrameRef.current?.querySelectorAll<HTMLElement>(".fc-timegrid-col[data-date], .fc-daygrid-day[data-date]") || []);
    const column = columns.find((element) => {
      const rect = element.getBoundingClientRect();
      return rect.width > 8 && clientX >= rect.left && clientX < rect.right;
    });
    return column?.dataset.date || null;
  }

  function storeAtClientX(clientX: number) {
    const columns = Array.from(calendarFrameRef.current?.querySelectorAll<HTMLElement>(".fc-timegrid-col[data-date]") || []);
    const column = columns.find((element) => {
      const rect = element.getBoundingClientRect();
      return rect.width > 8 && clientX >= rect.left && clientX < rect.right;
    });
    if (!column) return null;
    const activeStores = displayedStoresForDay(column, dayLocationPrefsRef.current);
    if (!activeStores.length) return null;
    const rect = column.getBoundingClientRect();
    const index = Math.min(activeStores.length - 1, Math.floor((clientX - rect.left) / rect.width * activeStores.length));
    return activeStores[index];
  }

  function storeAtPointerEvent(event: MouseEvent | TouchEvent | null) {
    if (!event) return null;
    const clientX = "changedTouches" in event ? event.changedTouches[0]?.clientX : event.clientX;
    return clientX == null ? null : storeAtClientX(clientX);
  }

  function nextFreeStore(start: Date, end: Date, currentStore: string) {
    const occupied = new Set((calendarRef.current?.getApi().getEvents() || [])
      .filter((event) => event.start && event.end && event.start < end && event.end > start)
      .map((event) => (event.extendedProps as EventRecord).storeName));
    return STORE_SUBCOLUMNS.find((name) => name !== currentStore && !occupied.has(name))
      || STORE_SUBCOLUMNS.find((name) => name !== currentStore)
      || currentStore;
  }

  function dateAtScrollerCenter(scroller = findHorizontalScroller()) {
    if (!scroller) return focusDateRef.current;
    const centerX = scroller.getBoundingClientRect().left + scroller.clientWidth / 2;
    const visibleColumn = rollingDayColumns(calendarFrameRef.current, dayWidthRef.current, overlapExpansionPercentRef.current, dayLocationPrefsRef.current)
      .find((column) => {
        const rect = column.element.getBoundingClientRect();
        return rect.width > 8 && centerX >= rect.left && centerX < rect.right;
      });
    if (visibleColumn) return new Date(`${visibleColumn.date}T12:00:00`);
    const fullDayWidth = dayWidthRef.current;
    const index = Math.floor((scroller.scrollLeft + scroller.clientWidth / 2) / fullDayWidth);
    return addLocalDays(rollingRangeStartRef.current, Math.max(0, index));
  }

  function scrollDateToCenter(date: Date, rangeStart = rollingRangeStartRef.current) {
    const scroller = findHorizontalScroller();
    if (!scroller) return;
    const dateKey = dateInput(date);
    const column = rollingDayColumns(calendarFrameRef.current, dayWidthRef.current, overlapExpansionPercentRef.current, dayLocationPrefsRef.current)
      .find((candidate) => candidate.date === dateKey)?.element;
    if (column) {
      const scrollerRect = scroller.getBoundingClientRect();
      const columnRect = column.getBoundingClientRect();
      const currentColumnCenter = columnRect.left - scrollerRect.left + scroller.scrollLeft + columnRect.width / 2;
      const nextLeft = currentColumnCenter - scroller.clientWidth / 2;
      scroller.scrollLeft = Math.max(0, Math.min(nextLeft, scroller.scrollWidth - scroller.clientWidth));
      calendarFrameRef.current?.style.setProperty("--rolling-scroll-left", `${scroller.scrollLeft}px`);
      return;
    }
    const index = localDayDistance(rangeStart, date);
    const fullDayWidth = dayWidthRef.current;
    const nextLeft = index * fullDayWidth - (scroller.clientWidth - fullDayWidth) / 2;
    scroller.scrollLeft = Math.max(0, Math.min(nextLeft, scroller.scrollWidth - scroller.clientWidth));
    calendarFrameRef.current?.style.setProperty("--rolling-scroll-left", `${scroller.scrollLeft}px`);
  }

  function positionInlineEditor() {
    const highlights = Array.from(calendarFrameRef.current?.querySelectorAll<HTMLElement>(".fc-highlight") || [])
      .map((element) => element.getBoundingClientRect())
      .filter((rect) => rect.width > 2 && rect.height > 2)
      .sort((first, second) => second.width * second.height - first.width * first.height);
    const rect = highlights[0];
    if (!rect) return;
    const viewportMargin = 8;
    const maxHeight = Math.max(240, Math.min(510, window.innerHeight - viewportMargin * 2));
    const editor = document.querySelector<HTMLElement>(".inline-event-editor");
    const editorHeight = Math.min(maxHeight, Math.max(240, editor?.scrollHeight || 510));
    const width = Math.min(window.innerWidth - 16, Math.min(420, Math.max(360, rect.width - 4)));
    const left = Math.max(viewportMargin, Math.min(rect.left + 2, window.innerWidth - width - viewportMargin));
    const top = Math.max(
      viewportMargin,
      Math.min(rect.top + 1, window.innerHeight - editorHeight - viewportMargin)
    );
    setInlineEditorStyle({
      left,
      top,
      width,
      minHeight: Math.min(350, maxHeight),
      maxHeight,
      visibility: "visible"
    });
  }

  function captureCenterBeforeZoom() {
    if (view === "rollingTimeGrid") focusDateRef.current = dateAtScrollerCenter();
  }

  const loadBootstrap = useCallback(async () => {
    try {
      const data = await request<Bootstrap>("/api/bootstrap");
      const sso = new URLSearchParams(window.location.search).get("sso");
      if (sso?.startsWith("/api/sso/authorize?")) {
        window.location.replace(sso);
        return;
      }
      setBootstrap(data);
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) setBootstrap(null);
      else setToast(error instanceof Error ? error.message : "无法载入系统");
    } finally {
      setAuthChecked(true);
    }
  }, []);

  const loadNotifications = useCallback(async () => {
    try {
      const data = await request<NotificationFeed>("/api/notifications");
      setNotificationFeed({ ...data, reviewTasks: data.reviewTasks || [] });
    } catch (error) {
      if (!(error instanceof ApiError && error.status === 401)) {
        setToast(error instanceof Error ? error.message : "无法载入日程通知");
      }
    }
  }, []);

  useEffect(() => { void loadBootstrap(); }, [loadBootstrap]);
  useEffect(() => {
    if (bootstrap) void loadNotifications();
    else setNotificationFeed({ retentionDays: 7, recentChanges: [], pendingAssignments: [], reviewTasks: [] });
  }, [bootstrap?.user.id, loadNotifications]);

  useEffect(() => {
    if (!bootstrap) return;
    const timer = window.setInterval(() => {
      if (document.visibilityState === "visible") void loadNotifications();
    }, 15_000);
    return () => window.clearInterval(timer);
  }, [bootstrap?.user.id, loadNotifications]);

  useEffect(() => {
    if (!bootstrap) return;
    return startVisibleRealtime({
      document,
      window,
      createStream: () => new EventSource("/api/stream"),
      refresh: () => {
        setReloadVersion((value) => value + 1);
        void loadBootstrap();
        void loadNotifications();
      },
      events: {
        "events-changed": () => {
          const ownBroadcast = ownEventBroadcastRef.current;
          if (ownBroadcast.count > 0 && Date.now() <= ownBroadcast.until) {
            ownBroadcast.count -= 1;
            return;
          }
          ownBroadcast.count = 0;
          setReloadVersion((value) => value + 1);
          void loadBootstrap();
        },
        "users-changed": () => void loadBootstrap(),
        "settings-changed": () => void loadBootstrap(),
        "notifications-changed": () => void loadNotifications()
      }
    });
  }, [bootstrap?.user.id, loadBootstrap, loadNotifications]);

  useEffect(() => {
    const calendar = calendarRef.current?.getApi();
    calendar?.refetchEvents();
  }, [reloadVersion, storeFilter, employeeFilter, typeFilters, search]);

  const storeVisualSettingsVersion = JSON.stringify([bootstrap?.storeColors, bootstrap?.storeDisabledTimes]);
  useEffect(() => {
    scheduleCrossOverlapLayout();
  }, [storeVisualSettingsVersion, storeFilter]);

  useEffect(() => {
    saveDayLocationPrefs(dayLocationPrefs);
    scheduleCrossOverlapLayout();
  }, [dayLocationPrefs]);

  // The menu is anchored to a header button, so it cannot outlive the calendar view it was opened in.
  useEffect(() => { setDayLocationMenu(null); }, [view]);

  function changeDayLocations(date: string, stores: StoreSubcolumn[] | null) {
    setDayLocationPrefs((current) => {
      const next = { ...current };
      if (stores?.length) next[date] = stores;
      else delete next[date];
      return next;
    });
  }

  useEffect(() => {
    if (!toast) return;
    const timeout = window.setTimeout(() => setToast(""), 3200);
    return () => window.clearTimeout(timeout);
  }, [toast]);

  useEffect(() => () => {
    copyHandleCleanupRef.current?.();
    if (crossOverlapFrameRef.current != null) window.cancelAnimationFrame(crossOverlapFrameRef.current);
  }, []);

  useEffect(() => {
    const relayout = () => scheduleCrossOverlapLayout();
    window.addEventListener("resize", relayout);
    return () => window.removeEventListener("resize", relayout);
  }, []);

  useEffect(() => {
    undoStackRef.current = [];
    eventMutationGenerationRef.current = 0;
    confirmedEventsRef.current.clear();
    pendingMovesRef.current.clear();
  }, [bootstrap?.user.id]);

  useEffect(() => {
    if (!bootstrap) return;
    const undoFromKeyboard = (event: KeyboardEvent) => {
      if (event.repeat || event.shiftKey || event.key.toLowerCase() !== "z" || (!event.metaKey && !event.ctrlKey)) return;
      const target = event.target as HTMLElement | null;
      if (target?.closest("input, textarea, select, [contenteditable='true']")) return;
      if (document.querySelector('[role="dialog"]') || undoShortcutHeldRef.current) return;
      const now = Date.now();
      if (now - lastUndoShortcutAtRef.current < 1200) return;
      lastUndoShortcutAtRef.current = now;
      undoShortcutHeldRef.current = true;
      event.preventDefault();
      undoLatestActionRef.current();
    };
    const releaseUndoShortcut = (event: KeyboardEvent) => {
      if (["z", "meta", "control"].includes(event.key.toLowerCase())) undoShortcutHeldRef.current = false;
    };
    const releaseOnBlur = () => { undoShortcutHeldRef.current = false; };
    window.addEventListener("keydown", undoFromKeyboard);
    window.addEventListener("keyup", releaseUndoShortcut);
    window.addEventListener("blur", releaseOnBlur);
    return () => {
      window.removeEventListener("keydown", undoFromKeyboard);
      window.removeEventListener("keyup", releaseUndoShortcut);
      window.removeEventListener("blur", releaseOnBlur);
    };
  }, [bootstrap?.user.id]);

  useEffect(() => {
    if (!eventHoverPreview) return;
    const hide = () => setEventHoverPreview(null);
    window.addEventListener("scroll", hide, true);
    window.addEventListener("resize", hide);
    return () => {
      window.removeEventListener("scroll", hide, true);
      window.removeEventListener("resize", hide);
    };
  }, [eventHoverPreview]);

  useEffect(() => {
    dayWidthRef.current = dayWidth;
    overlapExpansionPercentRef.current = overlapExpansionPercent;
    window.localStorage.setItem("ale-calendar-day-width-v3", String(dayWidth));
    window.localStorage.setItem("ale-calendar-overlap-expansion-v1", String(overlapExpansionPercent));
    const frame = window.requestAnimationFrame(() => {
      calendarRef.current?.getApi().updateSize();
      if (view === "rollingTimeGrid") scrollDateToCenter(focusDateRef.current);
      if (inlineDraft) positionInlineEditor();
      scheduleCrossOverlapLayout();
    });
    return () => window.cancelAnimationFrame(frame);
  }, [dayWidth, overlapExpansionPercent, view]);

  useEffect(() => {
    if (!bootstrap || !calendarFrameRef.current) return;
    const frame = calendarFrameRef.current;
    let touchDistance = 0;
    let touchWidth = dayWidthRef.current;
    const distance = (touches: TouchList) => Math.hypot(touches[0].clientX - touches[1].clientX, touches[0].clientY - touches[1].clientY);
    const wheel = (event: WheelEvent) => {
      if (event.ctrlKey) {
        event.preventDefault();
        captureCenterBeforeZoom();
        const change = Math.max(-18, Math.min(18, -event.deltaY * 0.45));
        setDayWidth((current) => Math.max(56, Math.min(220, Math.round(current + change))));
      } else if (view === "rollingTimeGrid" && Math.abs(event.deltaX) > 0.5) {
        event.preventDefault();
        frame.scrollLeft += event.deltaX;
      }
    };
    const touchStart = (event: TouchEvent) => {
      if (event.touches.length !== 2) return;
      touchDistance = distance(event.touches);
      touchWidth = dayWidthRef.current;
      captureCenterBeforeZoom();
    };
    const touchMove = (event: TouchEvent) => {
      if (event.touches.length !== 2 || !touchDistance) return;
      event.preventDefault();
      setDayWidth(Math.max(56, Math.min(220, Math.round(touchWidth * distance(event.touches) / touchDistance))));
    };
    frame.addEventListener("wheel", wheel, { passive: false });
    frame.addEventListener("touchstart", touchStart, { passive: true });
    frame.addEventListener("touchmove", touchMove, { passive: false });
    return () => {
      frame.removeEventListener("wheel", wheel);
      frame.removeEventListener("touchstart", touchStart);
      frame.removeEventListener("touchmove", touchMove);
    };
  }, [bootstrap?.user.id, view]);

  useEffect(() => {
    if (view !== "rollingTimeGrid") return;
    let scrollFrame = 0;
    let scroller: HTMLElement | null = null;
    const setupFrame = window.requestAnimationFrame(() => {
      scroller = findHorizontalScroller();
      if (!scroller) return;
      scrollDateToCenter(focusDateRef.current);
      const handleScroll = () => {
        window.cancelAnimationFrame(scrollFrame);
        scrollFrame = window.requestAnimationFrame(() => {
          if (!scroller) return;
          calendarFrameRef.current?.style.setProperty("--rolling-scroll-left", `${scroller.scrollLeft}px`);
          const centerDate = dateAtScrollerCenter(scroller);
          focusDateRef.current = centerDate;
          setSelectedDate(dateInput(centerDate));
          setCalendarTitle(rollingTitle(centerDate));
          if (inlineDraft) positionInlineEditor();
          const edge = dayWidthRef.current * 3;
          const nearEdge = scroller.scrollLeft < edge || scroller.scrollLeft > scroller.scrollWidth - scroller.clientWidth - edge;
          if (recenterTimeoutRef.current) window.clearTimeout(recenterTimeoutRef.current);
          if (nearEdge) {
            recenterTimeoutRef.current = window.setTimeout(() => {
              focusDateRef.current = centerDate;
              calendarRef.current?.getApi().gotoDate(centerDate);
            }, 180);
          }
        });
      };
      scroller.addEventListener("scroll", handleScroll, { passive: true });
      (scroller as HTMLElement & { _aleScrollHandler?: EventListener })._aleScrollHandler = handleScroll;
    });
    return () => {
      window.cancelAnimationFrame(setupFrame);
      window.cancelAnimationFrame(scrollFrame);
      if (recenterTimeoutRef.current) window.clearTimeout(recenterTimeoutRef.current);
      const handler = (scroller as (HTMLElement & { _aleScrollHandler?: EventListener }) | null)?._aleScrollHandler;
      if (scroller && handler) scroller.removeEventListener("scroll", handler);
    };
  }, [view, rangeVersion, dayWidth, inlineDraft?.startAt]);

  useEffect(() => {
    if (view !== "rollingTimeGrid") {
      setHourAxis(null);
      return;
    }
    const frame = calendarFrameRef.current;
    if (!frame) return;
    let syncFrame = 0;
    const sync = () => {
      const area = frame.parentElement?.getBoundingClientRect();
      const verticalScroller = frame.querySelector<HTMLElement>(".fc-scroller-liquid-absolute");
      if (!area || !verticalScroller) return;
      const visible = verticalScroller.getBoundingClientRect();
      const markers = Array.from(frame.querySelectorAll<HTMLElement>(".fc-timegrid-slot-label[data-time$=':00:00']"))
        .filter((element) => element.textContent?.trim())
        .map((element) => ({ label: element.textContent!.trim(), top: element.getBoundingClientRect().top - visible.top - 8 }));
      const next = { top: visible.top - area.top, height: visible.height, markers };
      setHourAxis((current) => JSON.stringify(current) === JSON.stringify(next) ? current : next);
    };
    const schedule = () => {
      window.cancelAnimationFrame(syncFrame);
      syncFrame = window.requestAnimationFrame(sync);
    };
    schedule();
    frame.addEventListener("scroll", schedule, true);
    window.addEventListener("resize", schedule);
    return () => {
      window.cancelAnimationFrame(syncFrame);
      frame.removeEventListener("scroll", schedule, true);
      window.removeEventListener("resize", schedule);
    };
  }, [view, rangeVersion, dayWidth, lateHoursExpanded]);

  useEffect(() => {
    if (!inlineDraft) return;
    const update = () => window.requestAnimationFrame(positionInlineEditor);
    update();
    calendarFrameRef.current?.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      calendarFrameRef.current?.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [inlineDraft?.startAt, dayWidth, view]);

  const fetchEvents = useCallback(async (info: { startStr: string; endStr: string }, success: (events: EventInput[]) => void, failure: (error: Error) => void) => {
    const currentBootstrap = bootstrapRef.current;
    if (!currentBootstrap) return success([]);
    const requestGeneration = eventMutationGenerationRef.current;
    try {
      const data = await request<{ events: EventRecord[] }>(`/api/events?start=${encodeURIComponent(info.startStr)}&end=${encodeURIComponent(info.endStr)}`);
      const normalizedSearch = search.trim().toLowerCase();
      const uniqueEvents = reconcileFetchedEvents(
        data.events,
        confirmedEventsRef.current,
        pendingMovesRef.current,
        info,
        requestGeneration
      );
      const events = uniqueEvents.filter((item) => {
        if (storeFilter && item.storeName !== storeFilter) return false;
        if (employeeFilter) {
          const selectedEmployeeId = Number(employeeFilter);
          if (item.employeeId !== selectedEmployeeId && !item.participantIds.includes(selectedEmployeeId)) return false;
        }
        if (typeFilters.size && !eventWorkTypes(item).some((type) => typeFilters.has(type))) return false;
        if (normalizedSearch && !`${item.title} ${item.clientProject} ${item.storeName} ${item.notes}`.toLowerCase().includes(normalizedSearch)) return false;
        return true;
      }).map((item) => calendarEventInput(item, currentBootstrap, { pending: item.id != null && pendingMovesRef.current.has(item.id) }));
      success(events);
    } catch (reason) { failure(reason instanceof Error ? reason : new Error("日程载入失败")); }
  }, [bootstrap?.user.id, bootstrap?.workTypeColors, storeFilter, employeeFilter, typeFilters, search]);

  if (!authChecked) return <div className="loading-screen"><CalendarDays size={28} /><span>正在载入工作日历…</span></div>;
  if (!bootstrap) return <Login onSuccess={loadBootstrap} />;

  const ready = bootstrap;
  const canWrite = ready.user.role !== "viewer";
  const defaultBranch = ready.user.branchId || ready.branches.find((item) => item.active)?.id || 1;

  function emptyEvent(selection?: { start: Date; end: Date }): EventRecord {
    const start = selection?.start || (() => { const date = new Date(); date.setMinutes(Math.ceil(date.getMinutes() / 30) * 30, 0, 0); return date; })();
    const end = selection?.end || new Date(start.getTime() + 60 * 60_000);
    const defaults = loadEventDefaults(ready);
    return {
      title: "", workType: ready.workTypes[0], workTypes: [ready.workTypes[0]], startAt: start.toISOString(), endAt: end.toISOString(), branchId: defaultBranch,
      employeeId: defaults.employeeId, participantIds: [], clientProject: "", location: "", storeName: defaults.storeName, pricingMode: "time", unitPrice: null, totalPrice: null, notes: "", status: "scheduled"
    };
  }

  function newEvent(selection?: { start: Date; end: Date }) {
    setInlineDraft(null);
    calendarRef.current?.getApi().unselect();
    setEventModal(emptyEvent(selection));
  }

  function rememberUndo(action: CalendarUndoAction) {
    undoStackRef.current = [...undoStackRef.current.slice(-19), action];
  }

  function rememberConfirmedEvent(value: EventRecord | null, id = value?.id) {
    if (id == null) return;
    eventMutationGenerationRef.current += 1;
    confirmedEventsRef.current.set(id, { event: value, generation: eventMutationGenerationRef.current });
  }

  function syncLatestUndoVersion(value: EventRecord) {
    if (!value.id) return;
    for (let index = undoStackRef.current.length - 1; index >= 0; index -= 1) {
      const action = undoStackRef.current[index];
      if (action.kind === "update" && action.after.id === value.id) {
        action.after = { ...action.after, version: value.version };
        return;
      }
      if (action.kind === "create" && action.event.id === value.id) {
        action.event = { ...action.event, version: value.version };
        return;
      }
    }
  }

  function remapUndoEventId(oldId: number | undefined, restored: EventRecord) {
    if (!oldId || !restored.id || oldId === restored.id) return;
    undoStackRef.current = undoStackRef.current.map((action) => {
      if (action.kind === "update") return {
        ...action,
        before: action.before.id === oldId ? { ...action.before, id: restored.id } : action.before,
        after: action.after.id === oldId ? { ...action.after, id: restored.id } : action.after
      };
      return action.event.id === oldId ? { ...action, event: { ...action.event, id: restored.id } } : action;
    });
    syncLatestUndoVersion(restored);
  }

  function eventMatchesCurrentFilters(value: EventRecord) {
    if (storeFilter && value.storeName !== storeFilter) return false;
    if (employeeFilter) {
      const selectedEmployeeId = Number(employeeFilter);
      if (value.employeeId !== selectedEmployeeId && !value.participantIds.includes(selectedEmployeeId)) return false;
    }
    if (typeFilters.size && !eventWorkTypes(value).some((type) => typeFilters.has(type))) return false;
    const normalizedSearch = search.trim().toLowerCase();
    return !normalizedSearch || `${value.title} ${value.clientProject} ${value.storeName} ${value.notes}`.toLowerCase().includes(normalizedSearch);
  }

  function syncCalendarProjectPricing(value: EventRecord, estimatedTotal = value.projectEstimatedTotal ?? null) {
    const projectKey = value.clientProject.trim().toLocaleLowerCase("zh-CN");
    if (!projectKey) return;
    for (const event of calendarRef.current?.getApi().getEvents() || []) {
      const eventValue = event.extendedProps as EventRecord;
      if (eventValue.clientProject.trim().toLocaleLowerCase("zh-CN") !== projectKey) continue;
      event.setExtendedProp("unitPrice", value.unitPrice);
      event.setExtendedProp("pricingMode", value.pricingMode);
      event.setExtendedProp("totalPrice", value.totalPrice);
      event.setExtendedProp("projectEstimatedTotalOverride", value.projectEstimatedTotalOverride ?? null);
      event.setExtendedProp("projectEstimatedTotal", estimatedTotal);
    }
  }

  function adjustProjectSummary(value: EventRecord, direction: 1 | -1) {
    if (value.status === "cancelled" || !value.clientProject.trim()) return;
    const project = value.clientProject.trim();
    const billableCount = value.collaborationRootEventId ? 0 : 1;
    const minutes = billableCount
      ? Math.max(0, Math.round((new Date(value.endAt).getTime() - new Date(value.startAt).getTime()) / 60_000))
      : 0;
    setBootstrap((current) => {
      if (!current) return current;
      const projectKey = project.toLocaleLowerCase("zh-CN");
      const index = current.projectSummaries.findIndex((item) => item.project.toLocaleLowerCase("zh-CN") === projectKey);
      if (direction === 1) {
        const projectSummaries = index < 0
          ? [{
            project,
            count: billableCount,
            totalMinutes: minutes,
            pricingMode: value.pricingMode,
            unitPrice: value.unitPrice,
            estimatedTotalOverride: value.projectEstimatedTotalOverride ?? null,
            estimatedTotal: value.projectEstimatedTotalOverride ?? projectEstimate(value.pricingMode, value.unitPrice, billableCount, minutes) ?? 0,
            totalAmount: value.totalPrice || 0
          }, ...current.projectSummaries]
          : current.projectSummaries.map((item, itemIndex) => itemIndex === index ? {
            ...item,
            count: item.count + billableCount,
            totalMinutes: item.totalMinutes + minutes,
            pricingMode: value.pricingMode,
            unitPrice: value.unitPrice,
            estimatedTotalOverride: value.projectEstimatedTotalOverride ?? null,
            estimatedTotal: value.projectEstimatedTotalOverride ?? projectEstimate(value.pricingMode, value.unitPrice, item.count + billableCount, item.totalMinutes + minutes) ?? 0,
            totalAmount: value.totalPrice || 0
          } : item);
        return {
          ...current,
          projectSummaries,
          projectNames: current.projectNames.some((name) => name.toLocaleLowerCase("zh-CN") === projectKey)
            ? current.projectNames
            : [project, ...current.projectNames]
        };
      }
      if (index < 0) return current;
      const previous = current.projectSummaries[index];
      const remainingCount = Math.max(0, previous.count - billableCount);
      const remainingMinutes = Math.max(0, previous.totalMinutes - minutes);
      return {
        ...current,
        projectSummaries: remainingCount === 0
          ? current.projectSummaries.filter((_, itemIndex) => itemIndex !== index)
          : current.projectSummaries.map((item, itemIndex) => itemIndex === index ? {
            ...item,
            count: remainingCount,
            totalMinutes: remainingMinutes,
            estimatedTotal: item.estimatedTotalOverride ?? projectEstimate(item.pricingMode, item.unitPrice, remainingCount, remainingMinutes) ?? 0
          } : item),
        projectNames: remainingCount === 0
          ? current.projectNames.filter((name) => name.toLocaleLowerCase("zh-CN") !== projectKey)
          : current.projectNames
      };
    });
  }

  async function undoLatestCalendarAction() {
    if (undoBusyRef.current || !undoStackRef.current.length) {
      if (!undoBusyRef.current) setToast("没有可撤销的日程操作");
      return;
    }
    const action = undoStackRef.current.pop()!;
    const calendar = calendarRef.current?.getApi();
    let transientEventId: string | null = null;
    undoBusyRef.current = true;
    ownEventBroadcastRef.current.count += 1;
    ownEventBroadcastRef.current.until = Date.now() + 5000;
    try {
      if (action.kind === "create") {
        const id = action.event.id;
        if (!id) throw new Error("这条日程无法撤销");
        removeCalendarEventsById(calendar, id);
        await request(`/api/events/${id}`, { method: "DELETE", headers: { "X-Calendar-Operation": "undo-create" } }, ready.csrfToken);
        rememberConfirmedEvent(null, id);
        adjustProjectSummary(action.event, -1);
      } else if (action.kind === "delete") {
        const { id: _id, version: _version, createdAt: _createdAt, updatedAt: _updatedAt, ...restoreValue } = action.event;
        const temporaryId = `undo-restore-${Date.now()}`;
        transientEventId = temporaryId;
        const temporaryEvent = eventMatchesCurrentFilters(restoreValue)
          ? addCalendarEventToSource(calendar, calendarEventInput(restoreValue, ready, { id: temporaryId, pending: true }))
          : null;
        const saved = await request<{ event: EventRecord }>("/api/events", {
          method: "POST",
          headers: { "X-Calendar-Operation": "undo-delete" },
          body: JSON.stringify(restoreValue)
        }, ready.csrfToken);
        temporaryEvent?.remove();
        rememberConfirmedEvent(saved.event);
        if (eventMatchesCurrentFilters(saved.event)) {
          replaceCalendarEvent(calendar, calendarEventInput(saved.event, ready));
        }
        syncCalendarProjectPricing(saved.event);
        adjustProjectSummary(saved.event, 1);
        remapUndoEventId(action.event.id, saved.event);
      } else {
        const id = action.after.id;
        if (!id) throw new Error("这条日程无法撤销");
        removeCalendarEventsById(calendar, id);
        const optimisticBefore = { ...action.before, id, version: action.after.version };
        const optimisticEvent = eventMatchesCurrentFilters(optimisticBefore)
          ? addCalendarEventToSource(calendar, calendarEventInput(optimisticBefore, ready))
          : null;
        const saved = await request<{ event: EventRecord }>(`/api/events/${id}`, {
          method: "PATCH",
          headers: { "X-Calendar-Operation": "undo-update" },
          body: JSON.stringify(optimisticBefore)
        }, ready.csrfToken);
        optimisticEvent?.remove();
        rememberConfirmedEvent(saved.event);
        if (eventMatchesCurrentFilters(saved.event)) {
          replaceCalendarEvent(calendar, calendarEventInput(saved.event, ready));
        }
        syncCalendarProjectPricing(saved.event);
        adjustProjectSummary(action.after, -1);
        adjustProjectSummary(saved.event, 1);
        syncLatestUndoVersion(saved.event);
      }
      setEventHoverPreview(null);
      setEventModal(null);
      setToast(action.kind === "create"
        ? `已撤销新建：已移除「${action.event.title}」`
        : action.kind === "delete"
          ? `已撤销删除：已恢复「${action.event.title}」`
          : `已撤销修改：「${action.before.title}」`);
    } catch (reason) {
      ownEventBroadcastRef.current.count = Math.max(0, ownEventBroadcastRef.current.count - 1);
      undoStackRef.current.push(action);
      if (transientEventId) removeCalendarEventsById(calendar, transientEventId);
      if (action.kind === "create" && action.event.id && !calendarEventsById(calendar, action.event.id).length && eventMatchesCurrentFilters(action.event)) {
        replaceCalendarEvent(calendar, calendarEventInput(action.event, ready));
      } else if (action.kind === "update" && action.after.id) {
        removeCalendarEventsById(calendar, action.after.id);
        if (eventMatchesCurrentFilters(action.after)) replaceCalendarEvent(calendar, calendarEventInput(action.after, ready));
      }
      setToast(reason instanceof Error ? reason.message : "无法撤销日程操作");
    } finally {
      undoBusyRef.current = false;
    }
  }

  undoLatestActionRef.current = () => { void undoLatestCalendarAction(); };

  async function saveEvent(value: EventRecord, operation?: string): Promise<void> {
    const isNew = !value.id;
    const calendar = calendarRef.current?.getApi();
    const previousEvent = value.id
      ? calendarEventsById(calendar, value.id)
        .map((event) => event.extendedProps as EventRecord)
        .sort((first, second) => Number(second.version || 0) - Number(first.version || 0))[0]
      : undefined;
    const method = isNew ? "POST" : "PATCH";
    const url = value.id ? `/api/events/${value.id}` : "/api/events";
    ownEventBroadcastRef.current.count += 1;
    ownEventBroadcastRef.current.until = Date.now() + 5000;
    let saved: { event: EventRecord };
    try {
      saved = await request<{ event: EventRecord }>(url, {
        method,
        headers: { "X-Calendar-Operation": operation || (isNew ? "create" : "edit") },
        body: JSON.stringify(value)
      }, ready.csrfToken);
    } catch (reason) {
      ownEventBroadcastRef.current.count = Math.max(0, ownEventBroadcastRef.current.count - 1);
      throw reason;
    }
    if (isNew) rememberEventDefaults(ready.user.id, { employeeId: value.employeeId, storeName: value.storeName });
    rememberConfirmedEvent(saved.event);
    if (saved.event.id != null) pendingMovesRef.current.delete(saved.event.id);
    if (eventMatchesCurrentFilters(saved.event)) {
      replaceCalendarEvent(calendar, calendarEventInput(saved.event, ready));
    } else removeCalendarEventsById(calendar, saved.event.id!);
    syncCalendarProjectPricing(saved.event);
    if (previousEvent && previousEvent.clientProject.trim().toLocaleLowerCase("zh-CN") !== saved.event.clientProject.trim().toLocaleLowerCase("zh-CN")) {
      const previousMinutes = Math.max(0, (new Date(previousEvent.endAt).getTime() - new Date(previousEvent.startAt).getTime()) / 60_000);
      const previousCount = previousEvent.collaborationRootEventId ? 0 : 1;
      syncCalendarProjectPricing(previousEvent, previousEvent.projectEstimatedTotalOverride ?? Math.max(0, Number(previousEvent.projectEstimatedTotal || 0) - Number(projectEstimate(previousEvent.pricingMode, previousEvent.unitPrice, previousCount, previousMinutes) || 0)));
    }
    if (previousEvent) adjustProjectSummary(previousEvent, -1);
    adjustProjectSummary(saved.event, 1);
    if (isNew && ready.user.role !== "staff") rememberUndo({ kind: "create", event: saved.event });
    else if (previousEvent) rememberUndo({ kind: "update", before: { ...previousEvent }, after: saved.event });
    setEventModal(null);
    setToast(isNew ? "日程已创建" : "日程已更新");
  }

  async function deleteEvent(id: number) {
    if (!window.confirm("确定删除这条日程吗？删除记录会保留在审计日志中。")) return;
    const calendar = calendarRef.current?.getApi();
    const matchingEvents = calendarEventsById(calendar, id);
    const deletedValue = matchingEvents
      .map((event) => event.extendedProps as EventRecord)
      .sort((first, second) => Number(second.version || 0) - Number(first.version || 0))[0]
      || (eventModal?.id === id ? eventModal : undefined);

    setEventHoverPreview(null);
    setEventModal(null);
    removeCalendarEventsById(calendar, id);
    ownEventBroadcastRef.current.count += 1;
    ownEventBroadcastRef.current.until = Date.now() + 5000;

    try {
      await request(`/api/events/${id}`, { method: "DELETE", headers: { "X-Calendar-Operation": "delete-button" } }, ready.csrfToken);
      rememberConfirmedEvent(null, id);
      if (deletedValue) {
        const deletedMinutes = Math.max(0, (new Date(deletedValue.endAt).getTime() - new Date(deletedValue.startAt).getTime()) / 60_000);
        const deletedCount = deletedValue.collaborationRootEventId ? 0 : 1;
        syncCalendarProjectPricing(deletedValue, deletedValue.projectEstimatedTotalOverride ?? Math.max(0, Number(deletedValue.projectEstimatedTotal || 0) - Number(projectEstimate(deletedValue.pricingMode, deletedValue.unitPrice, deletedCount, deletedMinutes) || 0)));
        adjustProjectSummary(deletedValue, -1);
        rememberUndo({ kind: "delete", event: deletedValue });
      }
      setToast("日程已删除");
    } catch (reason) {
      ownEventBroadcastRef.current.count = Math.max(0, ownEventBroadcastRef.current.count - 1);
      if (deletedValue) replaceCalendarEvent(calendar, calendarEventInput(deletedValue, ready));
      setToast(reason instanceof Error ? reason.message : "无法删除日程");
    }
  }

  async function persistCalendarMove(arg: EventDropArg | EventResizeDoneArg) {
    const original = arg.event.extendedProps as EventRecord;
    const id = Number(arg.event.id);
    if (pendingMovesRef.current.has(id)) {
      arg.revert();
      setSaveError(`「${original.title}」上一次移动仍在保存，请等待完成后再拖动。`);
      return;
    }
    const moved = {
      ...original,
      id,
      startAt: arg.event.start!.toISOString(),
      endAt: (arg.event.end || new Date(arg.event.start!.getTime() + 3600_000)).toISOString()
    };
    pendingMovesRef.current.set(id, moved);
    arg.el.classList.add("event-copy-saving");
    try {
      await saveEvent(moved, "drag-or-resize");
    } catch (reason) {
      pendingMovesRef.current.delete(id);
      arg.el.classList.remove("event-copy-saving");
      arg.revert();
      const calendar = calendarRef.current?.getApi();
      replaceCalendarEvent(calendar, calendarEventInput(original, ready));
      const message = reason instanceof TypeError ? "无法连接服务器" : reason instanceof Error ? reason.message : "无法更新时间";
      setSaveError(`「${original.title}」的移动未确认保存：${message}。卡片暂按原位置显示，正在核对服务器。`);
      const rangeStart = new Date(Math.min(new Date(original.startAt).getTime(), new Date(moved.startAt).getTime()) - 86_400_000).toISOString();
      const rangeEnd = new Date(Math.max(new Date(original.endAt).getTime(), new Date(moved.endAt).getTime()) + 86_400_000).toISOString();
      try {
        const latest = await request<{ events: EventRecord[] }>(`/api/events?start=${encodeURIComponent(rangeStart)}&end=${encodeURIComponent(rangeEnd)}`);
        const serverEvent = latest.events.find((event) => event.id === id);
        rememberConfirmedEvent(serverEvent || null, id);
        if (serverEvent) replaceCalendarEvent(calendar, calendarEventInput(serverEvent, ready));
        else removeCalendarEventsById(calendar, id);
        if (serverEvent?.startAt === moved.startAt && serverEvent.endAt === moved.endAt) {
          setSaveError("");
          setToast(`已从服务器确认「${original.title}」的新时间`);
        } else setSaveError(`「${original.title}」的移动未保存：${message}。已恢复服务器上的位置，请重试。`);
      } catch {
        setSaveError(`「${original.title}」的移动无法确认是否保存：${message}。卡片暂按原位置显示，网络恢复后请刷新核对。`);
      }
    } finally {
      window.requestAnimationFrame(() => scheduleCrossOverlapLayout());
    }
  }

  async function createCalendarCopy(original: EventRecord, startAt: string, endAt: string) {
    const { id: _id, version: _version, createdAt: _createdAt, updatedAt: _updatedAt, ...copy } = original;
    const copyValue: EventRecord = { ...copy, startAt, endAt };
    const calendar = calendarRef.current?.getApi();
    const temporaryId = `copy-saving-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const temporaryEvent = addCalendarEventToSource(calendar, calendarEventInput(copyValue, ready, { id: temporaryId, pending: true }));
    ownEventBroadcastRef.current.count += 1;
    ownEventBroadcastRef.current.until = Date.now() + 5000;
    try {
      const saved = await request<{ event: EventRecord }>("/api/events", {
        method: "POST",
        headers: { "X-Calendar-Operation": "copy-handle" },
        body: JSON.stringify({ ...copyValue, sourceEventId: original.id })
      }, ready.csrfToken);
      temporaryEvent?.remove();
      rememberConfirmedEvent(saved.event);
      replaceCalendarEvent(calendar, calendarEventInput(saved.event, ready));
      syncCalendarProjectPricing(saved.event);
      if (ready.user.role !== "staff") rememberUndo({ kind: "create", event: saved.event });
      adjustProjectSummary(saved.event, 1);
      setToast("已复制日程");
    } catch (reason) {
      temporaryEvent?.remove();
      ownEventBroadcastRef.current.count = Math.max(0, ownEventBroadcastRef.current.count - 1);
      setToast(reason instanceof Error ? reason.message : "无法复制日程");
    }
  }

  async function createProjectCollaboration(original: EventRecord, startAt: string, endAt: string) {
    if (!original.id) return;
    const collaboratorDraft: EventRecord = {
      ...original,
      id: undefined,
      version: undefined,
      startAt,
      endAt,
      employeeId: ready.user.id,
      collaborationRootEventId: original.collaborationRootEventId || original.id,
      isCollaborative: true
    };
    const calendar = calendarRef.current?.getApi();
    const temporaryId = `collaboration-saving-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const temporaryEvent = addCalendarEventToSource(calendar, calendarEventInput(collaboratorDraft, ready, { id: temporaryId, pending: true }));
    ownEventBroadcastRef.current.count += 1;
    ownEventBroadcastRef.current.until = Date.now() + 5000;
    try {
      const saved = await request<{ event: EventRecord; sourceEvent: EventRecord }>(`/api/events/${original.id}/collaborate`, {
        method: "POST",
        headers: { "X-Calendar-Operation": "project-collaboration" },
        body: JSON.stringify({ startAt, endAt })
      }, ready.csrfToken);
      temporaryEvent?.remove();
      rememberConfirmedEvent(saved.sourceEvent);
      rememberConfirmedEvent(saved.event);
      replaceCalendarEvent(calendar, calendarEventInput(saved.sourceEvent, ready));
      replaceCalendarEvent(calendar, calendarEventInput(saved.event, ready));
      syncCalendarProjectPricing(saved.event);
      if (!original.clientProject.trim()) adjustProjectSummary(saved.sourceEvent, 1);
      adjustProjectSummary(saved.event, 1);
      setEventModal(saved.event);
      setToast("协作日程已创建，负责人已自动设为你");
    } catch (reason) {
      temporaryEvent?.remove();
      ownEventBroadcastRef.current.count = Math.max(0, ownEventBroadcastRef.current.count - 1);
      setToast(reason instanceof Error ? reason.message : "无法参与项目协作");
    }
  }

  function beginCopyHandleDrag(value: EventRecord, event: React.PointerEvent<HTMLButtonElement>, collaboration = false) {
    if (!value.id || event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();
    copyHandleCleanupRef.current?.();
    setEventHoverPreview(null);

    const card = event.currentTarget.closest<HTMLElement>(".fc-event");
    const inner = card?.querySelector<HTMLElement>(".event-inner");
    if (!card || !inner) return;
    const rect = card.getBoundingClientRect();
    const originalStart = new Date(value.startAt);
    const originalEnd = new Date(value.endAt);
    const duration = Math.max(15 * 60_000, originalEnd.getTime() - originalStart.getTime());
    const minutesPerPixel = (duration / 60_000) / Math.max(1, rect.height);
    const originX = event.clientX;
    const originY = event.clientY;
    let moved = false;
    let moveFrame = 0;
    let latestDeltaX = 0;
    let latestDeltaY = 0;

    const ghost = inner.cloneNode(true) as HTMLElement;
    ghost.querySelectorAll(".event-card-action").forEach((button) => button.remove());
    ghost.classList.add("event-copy-ghost");
    ghost.removeAttribute("data-event-id");
    Object.assign(ghost.style, {
      position: "fixed",
      left: `${rect.left}px`,
      top: `${rect.top}px`,
      width: `${rect.width}px`,
      height: `${rect.height}px`,
      color: "#fff"
    });
    const badge = document.createElement("span");
    badge.className = "event-copy-ghost-label";
    badge.textContent = collaboration ? "参与协作" : "副本";
    ghost.appendChild(badge);
    document.body.appendChild(ghost);
    document.body.classList.add("calendar-handle-copy-active");
    beginCalendarInteraction();

    const move = (pointerEvent: PointerEvent) => {
      latestDeltaX = pointerEvent.clientX - originX;
      latestDeltaY = pointerEvent.clientY - originY;
      moved ||= Math.hypot(latestDeltaX, latestDeltaY) >= 5;
      if (moveFrame) return;
      moveFrame = window.requestAnimationFrame(() => {
        moveFrame = 0;
        ghost.style.transform = `translate3d(${latestDeltaX}px, ${latestDeltaY}px, 0)`;
      });
    };
    const cleanup = () => {
      if (moveFrame) window.cancelAnimationFrame(moveFrame);
      window.removeEventListener("pointermove", move, true);
      window.removeEventListener("pointerup", finish, true);
      window.removeEventListener("pointercancel", cancel, true);
      ghost.remove();
      document.body.classList.remove("calendar-handle-copy-active");
      endCalendarInteraction();
      if (copyHandleCleanupRef.current === cleanup) copyHandleCleanupRef.current = null;
    };
    const finish = (pointerEvent: PointerEvent) => {
      pointerEvent.preventDefault();
      pointerEvent.stopPropagation();
      const frame = calendarFrameRef.current?.getBoundingClientRect();
      const releasedInside = Boolean(frame
        && pointerEvent.clientX >= frame.left && pointerEvent.clientX <= frame.right
        && pointerEvent.clientY >= frame.top && pointerEvent.clientY <= frame.bottom);
      cleanup();
      if ((!moved && !collaboration) || !releasedInside) return;
      const targetStart = new Date(originalStart);
      const targetDate = calendarDateAtClientX(pointerEvent.clientX);
      if (targetDate) {
        const [year, month, day] = targetDate.split("-").map(Number);
        if (year && month && day) targetStart.setFullYear(year, month - 1, day);
      }
      const snappedMinutes = Math.round(((pointerEvent.clientY - originY) * minutesPerPixel) / 15) * 15;
      targetStart.setMinutes(targetStart.getMinutes() + snappedMinutes);
      const targetEnd = new Date(targetStart.getTime() + duration).toISOString();
      if (collaboration) void createProjectCollaboration(value, targetStart.toISOString(), targetEnd);
      else void createCalendarCopy(value, targetStart.toISOString(), targetEnd);
    };
    const cancel = () => cleanup();
    copyHandleCleanupRef.current = cleanup;
    window.addEventListener("pointermove", move, true);
    window.addEventListener("pointerup", finish, true);
    window.addEventListener("pointercancel", cancel, true);
  }

  function openEvent(arg: EventClickArg) {
    if (pickingDisabledTime) return;
    setEventHoverPreview(null);
    setInlineDraft(null);
    calendarRef.current?.getApi().unselect();
    const item = arg.event.extendedProps as EventRecord;
    setEventModal({ ...item, id: Number(arg.event.id), startAt: arg.event.start!.toISOString(), endAt: (arg.event.end || arg.event.start!).toISOString() });
  }

  function selectRange(arg: DateSelectArg) {
    if (!canWrite) return;
    if (pickingDisabledTime && ready.user.role === "admin") {
      setPickedDisabledRange({ ...newStoreDisabledDraft(disabledPickStartStoreRef.current || storeAtPointerEvent(arg.jsEvent) || storeFilter || disabledPickStore),
        mode: "once", startAt: localDateTime(arg.start.toISOString()), endAt: localDateTime(arg.end.toISOString()),
        date: dateInput(arg.start), weekday: arg.start.getDay(),
        startTime: localDateTime(arg.start.toISOString()).slice(11), endTime: localDateTime(arg.end.toISOString()).slice(11) });
      setPickingDisabledTime(false);
      disabledPickStartStoreRef.current = null;
      calendarRef.current?.getApi().unselect();
      return;
    }
    setEventModal(null);
    setInlineEditorStyle({ visibility: "hidden" });
    setInlineDraft({ ...emptyEvent({ start: arg.start, end: arg.end }),
      storeName: storeAtPointerEvent(arg.jsEvent) || "" });
    window.requestAnimationFrame(() => window.requestAnimationFrame(positionInlineEditor));
  }

  function cancelInlineDraft() {
    setInlineDraft(null);
    calendarRef.current?.getApi().unselect();
  }

  async function saveInlineDraft(value: EventRecord) {
    await saveEvent(value);
    setInlineDraft(null);
    calendarRef.current?.getApi().unselect();
  }

  function selectRangeOnExistingEvent(start: Date, end: Date, storeName = "") {
    setEventModal(null);
    setInlineEditorStyle({ visibility: "hidden" });
    setInlineDraft({ ...emptyEvent({ start, end }), storeName });
    calendarRef.current?.getApi().select({ start, end, allDay: false });
    window.requestAnimationFrame(() => window.requestAnimationFrame(positionInlineEditor));
  }

  function createOnDoubleClick(arg: DateClickArg) {
    if (!canWrite || pickingDisabledTime || arg.jsEvent.detail !== 2) return;
    arg.jsEvent.preventDefault();
    const start = new Date(arg.date);
    if (arg.allDay) start.setHours(9, 0, 0, 0);
    const end = new Date(start.getTime() + 60 * 60_000);
    if (arg.view.type.toLowerCase().includes("timegrid")) selectRangeOnExistingEvent(start, end, storeAtPointerEvent(arg.jsEvent) || "");
    else newEvent({ start, end });
  }

  function showEventHover(value: EventRecord, timeText: string, element: HTMLDivElement) {
    if (calendarInteractionActiveRef.current) return;
    highlightCollaborationGroup(value);
    const card = element.closest<HTMLElement>(".fc-event") || element;
    const details = element.querySelector<HTMLElement>(".event-card-details");
    const rect = card.getBoundingClientRect();
    const contentIsClipped = element.dataset.contentClipped === "true"
      || element.scrollHeight > element.clientHeight + 1
      || Boolean(details && details.scrollHeight > details.clientHeight + 1);
    if (!contentIsClipped && rect.height >= 96) return;
    const width = Math.min(340, Math.max(260, rect.width));
    const left = Math.max(8, Math.min(rect.left, window.innerWidth - width - 8));
    const placeBelow = window.innerHeight - rect.bottom >= 230;
    const style: React.CSSProperties = placeBelow
      ? { left, top: rect.bottom + 3, width }
      : { left, bottom: window.innerHeight - rect.top + 3, width };
    setEventHoverPreview({ value, timeText, style });
  }

  function changeView(next: string) {
    cancelInlineDraft();
    if (next === "rollingTimeGrid") focusDateRef.current = new Date(`${selectedDate}T12:00:00`);
    setView(next);
    calendarRef.current?.getApi().changeView(next);
  }

  function gotoDate(value: string) {
    cancelInlineDraft();
    focusDateRef.current = new Date(`${value}T12:00:00`);
    setSelectedDate(value);
    calendarRef.current?.getApi().gotoDate(value);
    setSidebarOpen(false);
  }

  function goToday() {
    cancelInlineDraft();
    focusDateRef.current = new Date();
    calendarRef.current?.getApi().today();
  }

  function shiftVisibleDays(days: number) {
    cancelInlineDraft();
    const next = addLocalDays(view === "rollingTimeGrid" ? focusDateRef.current : new Date(`${selectedDate}T12:00:00`), days);
    focusDateRef.current = next;
    calendarRef.current?.getApi().gotoDate(next);
  }

  function handleDatesSet(arg: DatesSetArg) {
    rollingRangeStartRef.current = arg.start;
    if (arg.view.type === "rollingTimeGrid") {
      setCalendarTitle(rollingTitle(focusDateRef.current));
      setSelectedDate(dateInput(focusDateRef.current));
      setRangeVersion((current) => current + 1);
    } else {
      setCalendarTitle(arg.view.title);
      setView(arg.view.type);
      setSelectedDate(dateInput(arg.view.currentStart));
    }
    scheduleCrossOverlapLayout();
  }

  async function logout() {
    await request("/api/logout", { method: "POST" }, ready.csrfToken);
    setBootstrap(null);
  }

  async function changePassword() {
    const currentPassword = window.prompt("请输入当前密码");
    if (!currentPassword) return;
    const newPassword = window.prompt("请输入新密码（至少 8 位）");
    if (!newPassword) return;
    try {
      await request("/api/account/password", { method: "POST", body: JSON.stringify({ currentPassword, newPassword }) }, ready.csrfToken);
      setToast("密码已修改，其他设备已退出登录");
    } catch (reason) { setToast(reason instanceof Error ? reason.message : "修改密码失败"); }
  }

  async function acknowledgeAssignment(eventId: number) {
    setAcknowledgingEventId(eventId);
    try {
      await request(`/api/assignments/${eventId}/acknowledge`, { method: "POST" }, ready.csrfToken);
      setNotificationFeed((current) => ({
        ...current,
        pendingAssignments: current.pendingAssignments.filter((item) => item.eventId !== eventId)
      }));
      setToast("已确认收到委派");
    } catch (reason) {
      setToast(reason instanceof Error ? reason.message : "无法确认委派");
      await loadNotifications();
    } finally {
      setAcknowledgingEventId(null);
    }
  }

  function toggleType(type: string) {
    setTypeFilters((current) => {
      const next = new Set(current);
      if (next.has(type)) next.delete(type); else next.add(type);
      return next;
    });
  }

  return (
    <div className="app-shell">
      <aside className={`sidebar ${sidebarOpen ? "open" : ""}`}>
        <div className="brand-row">
          <div className="brand-mark"><CalendarDays size={21} /></div>
          <div><strong>ALE 工作日历</strong><small>内部协作系统</small></div>
          <button className="icon-button sidebar-close" onClick={() => setSidebarOpen(false)}><X size={18} /></button>
        </div>
        {canWrite && <button className="new-event-button" onClick={() => { newEvent(); setSidebarOpen(false); }}><Plus size={18} />新建日程</button>}
        <SidebarCalendar selectedDate={selectedDate} onSelect={gotoDate} />
        <div className="sidebar-section">
          <div className="section-title"><span>店铺</span><Filter size={14} /></div>
          <label className="filter-row"><input type="radio" name="store" checked={!storeFilter} onChange={() => setStoreFilter("")} /><i className="filter-dot multi" />全部店铺</label>
          {bootstrap.storeNames.map((name) => <label className="filter-row" key={name}><input type="radio" name="store" checked={storeFilter === name} onChange={() => setStoreFilter(name)} /><i className="filter-dot store" style={{ background: bootstrap.storeColors[name] }} />{name}</label>)}
        </div>
        <div className="sidebar-section">
          <div className="section-title"><span>工作类型</span><Filter size={14} /></div>
          {bootstrap.workTypes.map((type) => <label className="filter-row" key={type}><input type="checkbox" checked={!typeFilters.size || typeFilters.has(type)} onChange={() => toggleType(type)} /><i className="filter-dot" style={{ background: bootstrap.workTypeColors[type] }} />{type}</label>)}
          {typeFilters.size > 0 && <button className="clear-filter" onClick={() => setTypeFilters(new Set())}>显示全部</button>}
        </div>
        <RecentChangeSidebar feed={notificationFeed} />
        <div className="sidebar-bottom">
          <a className="sidebar-review-link" href="/capture/?view=inbox" target="_blank" rel="noreferrer"><Check size={17} />我的待办 / 快速添加</a>
          <a className="sidebar-review-link" href="/audio-review/" target="_blank" rel="noreferrer"><AudioLines size={17} />ALE 审阅台</a>
          <button onClick={() => { setReportOpen(true); setSidebarOpen(false); }}><BarChart3 size={17} />工作记录表</button>
          {bootstrap.user.role === "admin" && <button onClick={() => { setTeamOpen(true); setSidebarOpen(false); }}><Settings size={17} />设置</button>}
        </div>
      </aside>
      {sidebarOpen && <button className="sidebar-scrim" aria-label="关闭侧边栏" onClick={() => setSidebarOpen(false)} />}
      <main className="calendar-area">
        <header className="topbar">
          <div className="nav-group">
            <button className="icon-button mobile-menu" onClick={() => setSidebarOpen(true)}><Menu size={19} /></button>
            <button className="today-button" onClick={goToday}>今天</button>
            <div className="arrow-pair"><button onClick={() => shiftVisibleDays(view === "rollingTimeGrid" ? -7 : -1)} aria-label="向前移动"><ChevronLeft size={19} /></button><button onClick={() => shiftVisibleDays(view === "rollingTimeGrid" ? 7 : 1)} aria-label="向后移动"><ChevronRight size={19} /></button></div>
            <h1>{calendarTitle}</h1>
          </div>
          <ActionNotifications feed={notificationFeed} acknowledgingEventId={acknowledgingEventId} onAcknowledge={acknowledgeAssignment} />
          <div className="top-actions">
            <a className="audio-review-button" href="/audio-review/" target="_blank" rel="noreferrer"><AudioLines size={16} />审阅台</a>
            <label className="search-box"><Search size={16} /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="搜索日程" /></label>
            <select className="employee-filter" value={employeeFilter} onChange={(event) => setEmployeeFilter(event.target.value)} aria-label="筛选负责人"><option value="">全部负责人</option>{bootstrap.users.filter((item) => item.active).map((item) => <option value={item.id} key={item.id}>{item.displayName}</option>)}</select>
            <div className="segmented view-switcher">
              <button className={view === "timeGridDay" ? "active" : ""} onClick={() => changeView("timeGridDay")}>日</button>
              <button className={view === "rollingTimeGrid" ? "active" : ""} onClick={() => changeView("rollingTimeGrid")} title="横向连续浏览前后日期">连续</button>
              <button className={view === "dayGridMonth" ? "active" : ""} onClick={() => changeView("dayGridMonth")}>月</button>
              <button className={view === "listMonth" ? "active" : ""} onClick={() => changeView("listMonth")}>列表</button>
            </div>
            {view === "rollingTimeGrid" && (
              <div className="calendar-width-controls">
                <label className="day-zoom-control" title="也可以在日历上用双指捏合缩放每日宽度">
                  <span>日宽</span>
                  <input
                    type="range"
                    min="56"
                    max="220"
                    step="4"
                    value={dayWidth}
                    aria-label="每日宽度"
                    aria-valuetext={`${dayWidth}px`}
                    onPointerDown={captureCenterBeforeZoom}
                    onChange={(event) => setDayWidth(Number(event.target.value))}
                  />
                </label>
                <label className="day-zoom-control overlap-zoom-control" title="同一地点出现并列日程时增加子列宽度">
                  <span>同地点并列</span>
                  <input
                    type="range"
                    min="25"
                    max="150"
                    step="5"
                    value={overlapExpansionPercent}
                    aria-label="同地点并列日程加宽比例"
                    aria-valuetext={`增加 ${overlapExpansionPercent}%`}
                    onPointerDown={captureCenterBeforeZoom}
                    onChange={(event) => setOverlapExpansionPercent(Number(event.target.value))}
                  />
                </label>
              </div>
            )}
            <div className="profile-menu">
              <button className="profile-button"><span>{bootstrap.user.displayName.slice(0, 1)}</span><div><strong>{bootstrap.user.displayName}</strong><small>{roleLabel(bootstrap.user.role)}</small></div></button>
              <div className="profile-popover">
                <a className="sidebar-review-link" href="/capture/?view=inbox" target="_blank" rel="noreferrer"><Check size={15} />我的待办 / 快速添加</a>
                <button onClick={() => void changePassword()}><Settings size={15} />修改密码</button>
                <button onClick={() => void logout()}><LogOut size={15} />退出登录</button>
              </div>
            </div>
          </div>
        </header>
        <div className="mobile-filters">
          <div className="segmented view-switcher"><button className={view === "timeGridDay" ? "active" : ""} onClick={() => changeView("timeGridDay")}>日</button><button className={view === "rollingTimeGrid" ? "active" : ""} onClick={() => changeView("rollingTimeGrid")}>连续</button><button className={view === "dayGridMonth" ? "active" : ""} onClick={() => changeView("dayGridMonth")}>月</button><button className={view === "listMonth" ? "active" : ""} onClick={() => changeView("listMonth")}>列表</button></div>
          {canWrite && <button className="mobile-add" onClick={() => newEvent()}><Plus size={19} /></button>}
        </div>
        {pickingDisabledTime && <div className="disabled-pick-banner" role="status">
          <strong>拉取不可用时段</strong><span>在棚位列上拖选时间，松开后返回设置。</span>
          <label>默认棚位 <select aria-label="拉取默认棚位" value={disabledPickStore} onChange={(event) => setDisabledPickStore(event.target.value)}>{ready.storeNames.map((name) => <option key={name}>{name}</option>)}</select></label>
          <button type="button" className="secondary-button" onClick={() => { setPickingDisabledTime(false); calendarRef.current?.getApi().unselect(); }}>取消拉取</button>
        </div>}
        <section
          onPointerDownCapture={(event) => { if (pickingDisabledTime) disabledPickStartStoreRef.current = storeAtClientX(event.clientX); }}
          className={`calendar-frame ${view === "rollingTimeGrid" ? "rolling-calendar location-columns" : view === "timeGridDay" ? "location-columns" : ""}`}
          ref={calendarFrameRef}
          style={view === "rollingTimeGrid" ? { "--rolling-calendar-width": `${dayWidth * 56 + ROLLING_TIME_AXIS_WIDTH}px` } as React.CSSProperties : undefined}
        >
          <FullCalendar
            ref={calendarRef}
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin]}
            locale={zhCnLocale}
            initialView={view}
            views={{
              rollingTimeGrid: {
                type: "timeGrid",
                visibleRange: rollingVisibleRange,
                dateIncrement: { days: 7 }
              }
            }}
            headerToolbar={false}
            height="100%"
            allDayText="全天"
            firstDay={1}
            nowIndicator
            selectable={canWrite}
            selectMirror
            selectOverlap
            selectMinDistance={CREATE_DRAG_MIN_DISTANCE}
            slotEventOverlap={false}
            editable={canWrite && !pickingDisabledTime}
            eventResizableFromStart
            eventStartEditable={canWrite && !pickingDisabledTime}
            eventDurationEditable={canWrite && !pickingDisabledTime}
            selectLongPressDelay={350}
            eventLongPressDelay={350}
            slotMinTime="07:00:00"
            slotMaxTime={lateHoursExpanded ? "26:00:00" : "24:00:00"}
            slotLabelContent={(arg) => arg.date.getHours() < 7 ? `次日 ${arg.text}` : arg.text}
            scrollTime="08:00:00"
            slotDuration="00:30:00"
            snapDuration="00:15:00"
            slotLabelInterval="01:00"
            dayHeaderFormat={{ weekday: "short", month: "numeric", day: "numeric" }}
            dayHeaderContent={(arg) => arg.view.type.toLowerCase().includes("timegrid")
              ? <div className="location-day-header"><span className="location-day-date">{arg.text}</span>
                  <button
                    type="button"
                    className="day-location-toggle"
                    title="展开地点列，选择显示哪些地点"
                    aria-label={`${arg.text} 地点列`}
                    // Capture phase: FullCalendar turns a click anywhere in the header link into "go to this day".
                    onClickCapture={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      const date = dateInput(arg.date);
                      setDayLocationMenu((current) => current?.date === date ? null : { date, title: arg.text });
                    }}
                  ><Columns3 size={12} /></button>
                  <div className="location-day-stores">
                  {STORE_SUBCOLUMNS.map((name) => <span key={name} data-store-name={name} title={name} hidden style={{ "--store-color": bootstrap.storeColors[name] } as React.CSSProperties}><i className="location-store-edge" aria-hidden="true" />{name}</span>)}
                </div></div>
              : arg.text}
            dayMaxEvents={4}
            navLinks
            weekNumbers={false}
            events={fetchEvents}
            select={selectRange}
            dateClick={createOnDoubleClick}
            eventClick={openEvent}
            eventDrop={(arg) => void persistCalendarMove(arg)}
            eventResize={(arg) => void persistCalendarMove(arg)}
            eventDragStart={() => {
              setEventHoverPreview(null);
              beginCalendarInteraction();
            }}
            eventDragStop={() => {
              endCalendarInteraction();
            }}
            eventResizeStart={() => {
              setEventHoverPreview(null);
              beginCalendarInteraction();
            }}
            eventResizeStop={() => {
              endCalendarInteraction();
            }}
            datesSet={handleDatesSet}
            eventContent={(arg) => {
              const storedValue = arg.event.extendedProps as EventRecord;
              const value: EventRecord = {
                ...storedValue,
                startAt: arg.event.start?.toISOString() || storedValue.startAt,
                endAt: arg.event.end?.toISOString() || storedValue.endAt
              };
              if (!value.workType && !value.workTypes?.length) return <div className="event-inner selection-mirror"><span>{arg.timeText}</span><strong>{pickingDisabledTime ? "不可用时段" : "新日程"}</strong></div>;
              const canMoveOrCopy = canMoveOrCopyEvent(value, ready);
              return (
                <EventCard
                  value={value}
                  timeText={arg.timeText}
                  bootstrap={ready}
                  onHover={(element) => showEventHover(value, arg.timeText, element)}
                  onLeave={() => {
                    setEventHoverPreview(null);
                    clearCollaborationGroupHighlight();
                  }}
                  onAddOverlap={canWrite && !pickingDisabledTime && value.id && arg.view.type.toLowerCase().includes("timegrid")
                    ? () => selectRangeOnExistingEvent(
                        new Date(arg.event.start!),
                        new Date(arg.event.end || new Date(arg.event.start!.getTime() + 60 * 60_000)),
                        nextFreeStore(new Date(arg.event.start!), new Date(arg.event.end || new Date(arg.event.start!.getTime() + 60 * 60_000)), value.storeName)
                      )
                    : undefined}
                  onCollaboratePointerDown={canWrite && !pickingDisabledTime && value.id ? (event) => beginCopyHandleDrag(value, event, true) : undefined}
                  onCopyPointerDown={canMoveOrCopy && !pickingDisabledTime && value.id ? (event) => beginCopyHandleDrag(value, event) : undefined}
                  onExportProjectSchedule={value.clientProject.trim() ? () => setScheduleExportProject(value.clientProject.trim()) : undefined}
                  onDelete={!pickingDisabledTime && canDeleteEvent(ready) && value.id ? () => void deleteEvent(value.id!) : undefined}
                />
              );
            }}
            eventDidMount={(arg) => {
              if (canWrite) arg.el.title = "拖动卡片可改期；点击卡片可修改地点；右下角＋可新增同时间日程";
              const storeIndex = storeSubcolumnIndex((arg.event.extendedProps as EventRecord).storeName || "");
              const harness = arg.el.closest<HTMLElement>(".fc-timegrid-event-harness");
              if (harness) harness.dataset.locationIndex = storeIndex < 0 ? "unknown" : String(storeIndex);
              if (arg.isMirror) return;
              window.requestAnimationFrame(() => {
                if (calendarInteractionActiveRef.current) return;
                reconcileCalendarEventId(calendarRef.current?.getApi(), arg.event.id);
                scheduleCrossOverlapLayout();
              });
            }}
            eventWillUnmount={(arg) => {
              if (!arg.isMirror) scheduleCrossOverlapLayout();
            }}
          />
        </section>
        {(view === "rollingTimeGrid" || view === "timeGridDay") && <button
          type="button"
          className="late-hours-toggle"
          aria-expanded={lateHoursExpanded}
          onClick={() => setLateHoursExpanded((current) => !current)}
        >{lateHoursExpanded ? "收起凌晨 0–2 点" : "展开凌晨 0–2 点"}<span>次日时段</span></button>}
        {hourAxis && (
          <div className="hour-axis-overlay" aria-hidden="true" style={{ top: hourAxis.top, height: hourAxis.height }}>
            {hourAxis.markers.map((marker) => <span key={marker.label} style={{ top: marker.top }}>{marker.label}</span>)}
          </div>
        )}
        {inlineDraft && (
          <InlineEventEditor
            value={inlineDraft}
            bootstrap={ready}
            style={inlineEditorStyle}
            onCancel={cancelInlineDraft}
            onSave={saveInlineDraft}
          />
        )}
        {eventHoverPreview && <EventHoverPreview preview={eventHoverPreview} bootstrap={ready} />}
        {dayLocationMenu && (
          <DayLocationMenu
            date={dayLocationMenu.date}
            title={dayLocationMenu.title}
            prefs={dayLocationPrefs}
            storeColors={ready.storeColors}
            frame={calendarFrameRef.current}
            onChange={changeDayLocations}
            onClose={() => setDayLocationMenu(null)}
          />
        )}
      </main>
      {eventModal && <EventModal
        value={eventModal}
        bootstrap={bootstrap}
        readOnly={eventModal.id ? !canFullyModifyEvent(eventModal, bootstrap) : !canWrite}
        onClose={() => setEventModal(null)}
        onSave={saveEvent}
        onDelete={eventModal.id && canDeleteEvent(bootstrap) ? deleteEvent : null}
        onExportProjectSchedule={eventModal.id && eventModal.clientProject.trim() ? (project) => { setEventModal(null); setScheduleExportProject(project); } : null}
      />}
      {scheduleExportProject && <ProjectScheduleExportDialog project={scheduleExportProject} onClose={() => setScheduleExportProject("")} />}
      {reportOpen && <ReportModal bootstrap={bootstrap} onClose={() => setReportOpen(false)} />}
      {teamOpen && <TeamModal bootstrap={bootstrap} picking={pickingDisabledTime} pickedRange={pickedDisabledRange} onPick={(storeName) => {
        cancelInlineDraft();
        setEventModal(null);
        setEventHoverPreview(null);
        setDayLocationMenu(null);
        setDisabledPickStore(storeName);
        disabledPickStartStoreRef.current = null;
        if (view !== "rollingTimeGrid" && view !== "timeGridDay") changeView("timeGridDay");
        setPickingDisabledTime(true);
      }} onClose={() => { setTeamOpen(false); setPickingDisabledTime(false); setPickedDisabledRange(null); }} refresh={loadBootstrap} />}
      {toast && <div className="toast"><Check size={17} />{toast}</div>}
      {saveError && <div className="calendar-save-error" role="alert"><AlertTriangle size={18} /><span>{saveError}</span><button onClick={() => setSaveError("")} aria-label="关闭保存错误提示"><X size={17} /></button></div>}
    </div>
  );
}

export default App;
